/**
 * Help Content - Static data for all screens
 * All text in Spanish (es-MX)
 */

import type { PageHelpContent } from '../types';

export const CREDIT_DASHBOARD_HELP: PageHelpContent = {
  pageKey: 'credit-dashboard',
  title: 'Panel de Credito',
  description:
    'Vista general del portafolio crediticio. Muestra indicadores clave de rendimiento (KPIs), el embudo de originacion y la actividad semanal de creditos originados.',
  capabilities: [
    'Ver el total de creditos activos y monto desembolsado',
    'Monitorear solicitudes pendientes y ofertas activas',
    'Analizar el embudo de conversion de usuarios',
    'Revisar la originacion semanal de creditos',
  ],
  tooltips: [
    {
      key: 'total_active_credits',
      tooltip: 'Numero total de creditos con estado ACTIVE en el portafolio',
    },
    {
      key: 'total_disbursed_mxn',
      tooltip: 'Suma total de montos desembolsados en pesos mexicanos',
    },
    {
      key: 'total_applications_pending',
      tooltip: 'Solicitudes en estados CREATED o PENDING_SIGNATURE',
    },
    {
      key: 'total_active_offers',
      tooltip: 'Ofertas en estado VIEWING o LOCKED que aun no han expirado',
    },
    {
      key: 'avg_credit_amount_mxn',
      tooltip: 'Promedio del monto de creditos activos en MXN',
    },
    {
      key: 'avg_term_weeks',
      tooltip: 'Promedio del plazo en semanas de creditos activos',
    },
    {
      key: 'funnel_stage',
      tooltip: 'Etapa del proceso de nurturing del usuario',
    },
    {
      key: 'conversion_rate_pct',
      tooltip: 'Porcentaje de usuarios que avanzan a la siguiente etapa',
    },
    {
      key: 'credits_originated',
      tooltip: 'Cantidad de creditos nuevos originados en la semana',
    },
    {
      key: 'total_amount_mxn',
      tooltip: 'Monto total de creditos originados en la semana',
    },
  ],
  terms: [
    {
      term: 'KPI',
      definition:
        'Key Performance Indicator - Indicador clave de rendimiento que mide el exito del portafolio',
    },
    {
      term: 'Embudo de Originacion',
      definition:
        'Representacion visual del proceso desde que un usuario entra al sistema hasta que obtiene un credito',
    },
    {
      term: 'Originacion',
      definition:
        'Proceso completo de crear un nuevo credito: desde la oferta hasta el desembolso',
    },
  ],
  faq: [
    {
      question: 'Cada cuanto se actualizan los datos?',
      answer:
        'Los datos se actualizan automaticamente cada 30 segundos. Tambien puedes hacer click en "Actualizar" para refrescar manualmente.',
    },
    {
      question: 'Que significa si no hay datos de funnel?',
      answer:
        'Indica que aun no hay usuarios en el proceso de nurturing para el partner seleccionado.',
    },
  ],
};

export const CREDITS_LIST_HELP: PageHelpContent = {
  pageKey: 'credits-list',
  title: 'Creditos del Portafolio',
  description:
    'Lista completa de creditos: activos, morosos, defaults, liquidados, cancelados y reestructurados. Es la tabla maestra desde la que se navega al detalle de cualquier credito.',
  capabilities: [
    'Buscar por telefono del cliente o por ID de credito',
    'Filtrar por estado (Activo, Moroso, Default, etc.) y por nivel (Bronze/Silver/Gold/Platinum)',
    'Combinar ambos filtros (ej. solo morosos nivel Bronze)',
    'Click en cualquier fila para abrir el detalle completo del credito',
    'Ordenar por cualquier columna haciendo click en el header',
  ],
  statusFlow: {
    label: 'Ciclo de vida de un credito',
    steps: [
      { value: 'ACTIVE', color: 'green', description: 'Vigente y al corriente. Default al ser desembolsado.' },
      { value: 'DELINQUENT', color: 'amber', description: 'Tiene cupones vencidos sin pagar.' },
      { value: 'DEFAULT', color: 'red', description: 'Excedio el umbral de mora (8 semanas) → baja contable.' },
      { value: 'PAID_OFF', color: 'blue', description: 'Liquidado al 100% (camino feliz).' },
    ],
  },
  sections: [
    {
      badge: 'Filtros',
      badgeColor: 'blue',
      title: 'Como filtrar la lista',
      description: 'Los dos selectores se combinan con AND — si eliges DELINQUENT + BRONZE, ves solo morosos del nivel Bronze.',
      items: [
        {
          label: 'Filtrar por Estado',
          description: 'Util para vistas operativas: cobranza diaria filtra por DELINQUENT; auditoria filtra por DEFAULT; reportes de exito filtran por PAID_OFF.',
        },
        {
          label: 'Filtrar por Nivel',
          description: 'Util para analisis de riesgo: si Bronze concentra mas mora que Gold, indica que el scoring del nivel inicial necesita revision.',
        },
        {
          label: 'Search por telefono / ID',
          description: 'Cuando ya sabes a que cliente o credito quieres llegar, escribe en la barra de busqueda — filtra en tiempo real.',
        },
      ],
    },
    {
      badge: 'Ciclo de vida',
      badgeColor: 'purple',
      title: 'Estados que puede tener un credito',
      description: 'Un credito nace ACTIVE al desembolsarse. Desde ahi puede tomar varios caminos hasta cerrarse:',
      items: [
        {
          label: 'Camino feliz', icon: 'check',
          description: 'ACTIVE → PAID_OFF. El cliente paga todos los cupones a tiempo y el credito se liquida.',
        },
        {
          label: 'Camino con tropiezo', icon: 'warning',
          description: 'ACTIVE → DELINQUENT → ACTIVE. El cliente atrasa un pago, luego se pone al corriente. Es lo mas comun.',
        },
        {
          label: 'Camino fallido', icon: 'error',
          description: 'ACTIVE → DELINQUENT → DEFAULT. Si la mora se sostiene 8 semanas consecutivas (configurable), se marca como baja contable.',
        },
        {
          label: 'Liquidacion voluntaria', icon: 'check',
          description: 'ACTIVE → CANCELLED. El cliente liquida anticipadamente o cancela en los primeros 7 dias post-desembolso.',
        },
        {
          label: 'Reestructura', icon: 'warning',
          description: 'ACTIVE → RESTRUCTURED → ACTIVE. Se renegocian condiciones (plazo / monto). Maximo 1 reestructura por credito para evitar bicicletas crediticias.',
        },
      ],
    },
  ],
  tooltips: [
    { key: 'credit_id', tooltip: 'Identificador unico del credito en el sistema' },
    { key: 'phone_number', tooltip: 'Numero de telefono del usuario asociado al credito' },
    { key: 'amount_mxn', tooltip: 'Monto original del credito en pesos mexicanos' },
    { key: 'term_weeks', tooltip: 'Plazo total del credito expresado en semanas' },
    { key: 'weekly_payment_mxn', tooltip: 'Monto del pago semanal que debe realizar el usuario' },
    { key: 'credit_level', tooltip: 'Nivel crediticio del usuario al momento del desembolso' },
    { key: 'status', tooltip: 'Estado actual del credito en el ciclo de vida' },
    { key: 'disbursed_at', tooltip: 'Fecha en que se desembolso el credito al usuario' },
  ],
  statuses: [
    { value: 'ACTIVE', label: 'Activo', description: 'El credito esta vigente y al corriente en sus pagos.', variant: 'success' },
    { value: 'DELINQUENT', label: 'Moroso', description: 'Tiene cupones vencidos sin pagar — entre 1 y 7 semanas de atraso.', variant: 'warning' },
    { value: 'DEFAULT', label: 'Default', description: '8 semanas o mas de mora consecutiva. Baja contable. Cobranza pasa a modo latente.', variant: 'danger' },
    { value: 'PAID_OFF', label: 'Liquidado', description: 'Pagado al 100%. Camino feliz del credito.', variant: 'info' },
    { value: 'CANCELLED', label: 'Cancelado', description: 'Liquidado anticipadamente o cancelado en los 7 dias post-desembolso (sin penalizacion).', variant: 'default' },
    { value: 'RESTRUCTURED', label: 'Reestructurado', description: 'Renegociado con nuevas condiciones. Maximo 1 reestructura por credito.', variant: 'default' },
  ],
  terms: [
    { term: 'Bronze', definition: 'Nivel inicial. Tasas mas altas, montos y plazos mas conservadores. Score interno bajo.' },
    { term: 'Silver', definition: 'Segundo nivel. Mejores condiciones que Bronze. Se llega tras historial positivo.' },
    { term: 'Gold', definition: 'Tercer nivel. Montos altos y tasas competitivas. Reservado a clientes con buen historial.' },
    { term: 'Platinum', definition: 'Nivel maximo. Mejores condiciones disponibles. Score interno alto sostenido.' },
    { term: 'Cupon', definition: 'Pago semanal individual del calendario de amortizacion. Un credito de 12 semanas tiene 12 cupones.' },
  ],
  faq: [
    { question: '¿Por que no veo todos los creditos al entrar?', answer: 'Por defecto la tabla muestra todos los creditos sin filtro. Si entraste con un filtro pre-aplicado (ej. desde el Pipeline → Bronze), revisa los selectores arriba.' },
    { question: '¿Como bajo a Excel?', answer: 'Por ahora la lista solo se ve en pantalla. Si necesitas exportar, copia los datos visibles o pide al equipo agregar el boton de export.' },
    { question: '¿Que pasa si un credito esta DELINQUENT y luego paga?', answer: 'Vuelve a ACTIVE automaticamente al recibir el pago que cubre el cupon vencido. El historial queda registrado.' },
    { question: '¿Puedo ver creditos de un cliente especifico?', answer: 'Si — busca por telefono en la barra. O entra al expediente del cliente desde Clientes para ver todos sus creditos juntos.' },
  ],
};

export const CREDIT_DETAIL_HELP: PageHelpContent = {
  pageKey: 'credit-detail',
  title: 'Detalle de Credito',
  description:
    'Vista completa de un credito individual. Incluye informacion del usuario, datos financieros, split Fiado/UNE-Credito y tabla de amortizacion.',
  capabilities: [
    'Ver datos del usuario (telefono, directory ID)',
    'Consultar condiciones del credito (monto, plazo, tasa)',
    'Revisar el split de intereses entre Fiado y UNE-Credito',
    'Ver la tabla de amortizacion completa',
    'Navegar al perfil financiero del usuario',
  ],
  tooltips: [
    {
      key: 'amount_mxn',
      tooltip: 'Monto original desembolsado del credito',
    },
    {
      key: 'term_weeks',
      tooltip: 'Plazo total del credito en semanas',
    },
    {
      key: 'annual_rate',
      tooltip: 'Tasa de interes anualizada del credito',
    },
    {
      key: 'weekly_payment_mxn',
      tooltip: 'Pago semanal fijo que debe realizar el usuario',
    },
    {
      key: 'total_interest_mxn',
      tooltip: 'Suma total de intereses durante la vida del credito',
    },
    {
      key: 'total_payable_mxn',
      tooltip: 'Monto total a pagar = capital + intereses totales',
    },
    {
      key: 'fiado_rate_points',
      tooltip: 'Porcentaje de la tasa que corresponde a Fiado',
    },
    {
      key: 'partner_rate_points',
      tooltip: 'Porcentaje de la tasa que corresponde a UNE-Credito',
    },
    {
      key: 'fiado_interest_mxn',
      tooltip: 'Monto total de intereses que recibe Fiado',
    },
    {
      key: 'partner_interest_mxn',
      tooltip: 'Monto total de intereses que recibe UNE-Credito',
    },
    {
      key: 'disbursed_at',
      tooltip: 'Fecha en que se desembolso el credito',
    },
    {
      key: 'first_payment_date',
      tooltip: 'Fecha del primer pago programado',
    },
    {
      key: 'last_payment_date',
      tooltip: 'Fecha del ultimo pago programado',
    },
  ],
  statuses: [
    {
      value: 'PENDING',
      label: 'Pendiente',
      description: 'Cupon programado pero aun no vence.',
      variant: 'default',
    },
    {
      value: 'PAID',
      label: 'Pagado',
      description: 'Cupon pagado en su totalidad.',
      variant: 'success',
    },
    {
      value: 'PARTIAL',
      label: 'Pago Parcial',
      description: 'El usuario pago parcialmente.',
      variant: 'warning',
    },
    {
      value: 'OVERDUE',
      label: 'Vencido',
      description: 'La fecha de pago ya paso sin pago completo.',
      variant: 'danger',
    },
  ],
  terms: [
    {
      term: 'Split Fiado/UNE-Credito',
      definition:
        'Division de los intereses generados entre Fiado (operador) y UNE-Credito (fondeador).',
    },
    {
      term: 'Tabla de Amortizacion',
      definition:
        'Calendario detallado de todos los pagos del credito.',
    },
    {
      term: 'Cupon',
      definition:
        'Un pago individual dentro de la tabla de amortizacion.',
    },
  ],
};

export const APPLICATIONS_LIST_HELP: PageHelpContent = {
  pageKey: 'applications-list',
  title: 'Lista de Solicitudes',
  description:
    'Tabla con todas las solicitudes de credito. Una solicitud se crea cuando el usuario acepta una oferta y firma el contrato.',
  capabilities: [
    'Buscar solicitudes por telefono o ID',
    'Filtrar por estado de solicitud',
    'Ver la fecha de firma y creacion',
    'Navegar al detalle de la solicitud',
  ],
  tooltips: [
    {
      key: 'application_id',
      tooltip: 'Identificador unico de la solicitud',
    },
    {
      key: 'phone_number',
      tooltip: 'Telefono del usuario que solicito el credito',
    },
    {
      key: 'amount_mxn',
      tooltip: 'Monto solicitado en pesos mexicanos',
    },
    {
      key: 'term_weeks',
      tooltip: 'Plazo solicitado en semanas',
    },
    {
      key: 'status',
      tooltip: 'Estado actual de la solicitud',
    },
    {
      key: 'signed_at',
      tooltip: 'Fecha y hora en que el usuario firmo el contrato',
    },
    {
      key: 'created_at',
      tooltip: 'Fecha y hora de creacion de la solicitud',
    },
  ],
  statuses: [
    {
      value: 'CREATED',
      label: 'Creada',
      description: 'Solicitud recien creada. Pendiente de firma.',
      variant: 'warning',
    },
    {
      value: 'PENDING_SIGNATURE',
      label: 'Pendiente de Firma',
      description: 'Esperando firma electronica.',
      variant: 'warning',
    },
    {
      value: 'SIGNED',
      label: 'Firmada',
      description: 'Contrato firmado. Lista para desembolso.',
      variant: 'success',
    },
    {
      value: 'DISBURSED',
      label: 'Desembolsada',
      description: 'Credito desembolsado exitosamente.',
      variant: 'success',
    },
    {
      value: 'EXPIRED',
      label: 'Expirada',
      description: 'Solicitud expirada antes de completar el proceso.',
      variant: 'danger',
    },
    {
      value: 'CANCELLED',
      label: 'Cancelada',
      description: 'Solicitud cancelada.',
      variant: 'danger',
    },
  ],
};

export const APPLICATION_DETAIL_HELP: PageHelpContent = {
  pageKey: 'application-detail',
  title: 'Detalle de Solicitud',
  description: 'Vista completa de una solicitud de credito.',
  capabilities: [
    'Ver datos del usuario asociado',
    'Consultar condiciones del credito solicitado',
    'Ver enlace a la oferta original',
    'Abrir el contrato (si existe)',
    'Navegar al perfil financiero del usuario',
  ],
  tooltips: [
    {
      key: 'amount_mxn',
      tooltip: 'Monto del credito solicitado',
    },
    {
      key: 'term_weeks',
      tooltip: 'Plazo del credito en semanas',
    },
    {
      key: 'annual_rate',
      tooltip: 'Tasa de interes anualizada aplicada',
    },
    {
      key: 'weekly_payment_mxn',
      tooltip: 'Pago semanal calculado para el credito',
    },
    {
      key: 'offer_id',
      tooltip: 'ID de la oferta que origino esta solicitud',
    },
    {
      key: 'contract_view_url',
      tooltip: 'Enlace al contrato firmado (PDF)',
    },
    {
      key: 'expires_at',
      tooltip: 'Fecha limite para completar la firma',
    },
    {
      key: 'signed_at',
      tooltip: 'Fecha en que el usuario firmo el contrato',
    },
  ],
};

export const OFFERS_LIST_HELP: PageHelpContent = {
  pageKey: 'offers-list',
  title: 'Lista de Ofertas',
  description:
    'Tabla con todas las ofertas de credito generadas automaticamente cuando el perfil financiero cumple las reglas de elegibilidad.',
  capabilities: [
    'Buscar ofertas por telefono o ID',
    'Filtrar por estado y nivel crediticio',
    'Ver montos maximos y seleccionados',
    'Navegar al detalle de la oferta',
  ],
  tooltips: [
    {
      key: 'offer_id',
      tooltip: 'Identificador unico de la oferta',
    },
    {
      key: 'phone_number',
      tooltip: 'Telefono del usuario',
    },
    {
      key: 'credit_level',
      tooltip: 'Nivel crediticio del usuario al generar la oferta',
    },
    {
      key: 'max_amount_mxn',
      tooltip: 'Monto maximo que puede solicitar segun su nivel',
    },
    {
      key: 'selected_amount_mxn',
      tooltip:
        'Monto que el usuario selecciono (0 si no ha seleccionado)',
    },
    {
      key: 'status',
      tooltip: 'Estado actual de la oferta',
    },
    {
      key: 'offer_expires_at',
      tooltip: 'Fecha en que la oferta dejara de estar disponible',
    },
  ],
  statuses: [
    {
      value: 'GENERATED',
      label: 'Generada',
      description:
        'Oferta recien generada. Aun no vista por el usuario.',
      variant: 'default',
    },
    {
      value: 'VIEWING',
      label: 'Visualizando',
      description: 'El usuario esta viendo la oferta.',
      variant: 'info',
    },
    {
      value: 'LOCKED',
      label: 'Bloqueada',
      description: 'El usuario selecciono monto y plazo.',
      variant: 'warning',
    },
    {
      value: 'CONVERTED',
      label: 'Convertida',
      description: 'Se convirtio en solicitud de credito.',
      variant: 'success',
    },
    {
      value: 'EXPIRED',
      label: 'Expirada',
      description: 'Oferta expirada sin ser aceptada.',
      variant: 'danger',
    },
  ],
};

export const OFFER_DETAIL_HELP: PageHelpContent = {
  pageKey: 'offer-detail',
  title: 'Detalle de Oferta',
  description:
    'Vista completa de una oferta de credito con opciones de pago.',
  capabilities: [
    'Ver datos del usuario',
    'Consultar monto maximo y seleccionado',
    'Ver todas las opciones de pago',
    'Identificar la opcion recomendada',
    'Navegar al perfil financiero del usuario',
  ],
  tooltips: [
    {
      key: 'max_amount_mxn',
      tooltip: 'Monto maximo autorizado segun el nivel crediticio',
    },
    {
      key: 'selected_amount_mxn',
      tooltip: 'Monto que el usuario eligio',
    },
    {
      key: 'term_weeks',
      tooltip: 'Plazo de la opcion de pago en semanas',
    },
    {
      key: 'annual_rate',
      tooltip: 'Tasa de interes anualizada para esta opcion',
    },
    {
      key: 'weekly_payment_mxn',
      tooltip: 'Pago semanal para esta opcion',
    },
    {
      key: 'total_to_pay_mxn',
      tooltip: 'Monto total que pagaria el usuario',
    },
    {
      key: 'total_interest_mxn',
      tooltip: 'Total de intereses para esta opcion',
    },
    {
      key: 'savings_vs_longest',
      tooltip: 'Ahorro en intereses vs plazo mas largo',
    },
    {
      key: 'is_recommended',
      tooltip: 'Opcion recomendada por el sistema',
    },
  ],
};

export const FINANCIAL_PROFILE_HELP: PageHelpContent = {
  pageKey: 'financial-profile',
  title: 'Perfil Financiero',
  description:
    'Vista completa del perfil financiero de un usuario con 59+ campos organizados por categoria.',
  capabilities: [
    'Ver el score crediticio total y su desglose',
    'Analizar ingresos, gastos y ratios del usuario',
    'Revisar actividad de recargas a wallet Mexico',
    'Ver progreso de gamificacion y recomendaciones',
    'Verificar elegibilidad y razones de no elegibilidad',
  ],
  tooltips: [
    {
      key: 'score_total',
      tooltip:
        'Score crediticio total (0-100). Combinacion ponderada de los 5 sub-scores.',
    },
    {
      key: 'score_income_stability',
      tooltip: 'Score de estabilidad de ingresos (peso 30%).',
    },
    {
      key: 'score_payment_capacity',
      tooltip: 'Score de capacidad de pago (peso 25%).',
    },
    {
      key: 'score_mexico_transfers',
      tooltip: 'Score de transferencias a Mexico (peso 20%).',
    },
    {
      key: 'score_seniority',
      tooltip: 'Score de antiguedad (peso 15%).',
    },
    {
      key: 'score_income_level',
      tooltip: 'Score de nivel de ingresos (peso 10%).',
    },
    {
      key: 'monthly_income_usd',
      tooltip: 'Ingreso mensual promedio en USD (ultimos 90 dias)',
    },
    {
      key: 'monthly_expenses_usd',
      tooltip: 'Gastos mensuales promedio en USD',
    },
    {
      key: 'expense_income_ratio',
      tooltip: 'Ratio gastos/ingresos. Menor a 60% es bueno.',
    },
    {
      key: 'sends_to_mexico_wallet',
      tooltip: 'Si el usuario hace recargas a wallet MXN',
    },
    {
      key: 'monthly_wallet_loads_usd',
      tooltip: 'Monto mensual promedio de recargas',
    },
    {
      key: 'current_balance_usd',
      tooltip: 'Saldo actual de la cuenta en USD',
    },
    {
      key: 'months_in_platform',
      tooltip: 'Meses desde el registro en la plataforma',
    },
    {
      key: 'credit_level',
      tooltip: 'Nivel crediticio actual',
    },
    {
      key: 'is_eligible',
      tooltip: 'Si cumple todas las reglas de elegibilidad',
    },
    {
      key: 'has_active_credit',
      tooltip: 'Si ya tiene un credito activo',
    },
    {
      key: 'score_to_next_level',
      tooltip: 'Puntos que le faltan para subir de nivel',
    },
    {
      key: 'progress_pct',
      tooltip: 'Porcentaje de avance hacia el siguiente nivel',
    },
  ],
  terms: [
    {
      term: 'Score Crediticio',
      definition:
        'Puntuacion de 0 a 100 que resume la capacidad crediticia del usuario.',
    },
    {
      term: 'Ratio Gastos/Ingresos',
      definition:
        'Porcentaje de ingresos que se gasta. Menor a 60% = bueno.',
    },
    {
      term: 'Wallet MXN',
      definition: 'Monedero de pesos mexicanos dentro de la app.',
    },
    {
      term: 'Gamificacion',
      definition:
        'Sistema de niveles y puntos para mejores condiciones de credito.',
    },
  ],
  faq: [
    {
      question: 'Porque un usuario no es elegible?',
      answer:
        'Razones comunes: ingreso insuficiente, cuenta nueva, credito activo existente.',
    },
    {
      question: 'Cada cuanto se actualiza el perfil?',
      answer:
        'Se recalcula diariamente o ante eventos significativos.',
    },
  ],
};

export const POLICIES_HELP: PageHelpContent = {
  pageKey: 'policies',
  title: 'Politicas de Credito',
  description:
    'Configuracion central del motor de credito. Define el pipeline completo: a quien se le presta (elegibilidad), como se mide (scoring), como se clasifica (niveles), cuanto se cobra (tasas) y cuanto se arriesga (limites).',
  capabilities: [
    'Configurar las 5 fases del pipeline del motor de credito',
    'Ver y editar (con permiso) cada politica desde su tab dedicado',
    'Cada cambio queda versionado — ver Historial para auditar',
    'Los cambios afectan SOLO evaluaciones futuras (creditos existentes no se ven afectados)',
  ],
  statusFlow: {
    label: 'Pipeline del motor de credito',
    steps: [
      { value: 'ELEGIBILIDAD', color: 'blue', description: 'Filtro inicial — pasa o no pasa.' },
      { value: 'SCORING', color: 'cyan', description: 'Asigna puntaje 0-100 segun pesos.' },
      { value: 'NIVELES', color: 'purple', description: 'Bronze / Silver / Gold / Platinum segun score.' },
      { value: 'TASAS', color: 'amber', description: 'Tasa de interes y comision por nivel.' },
      { value: 'LIMITES', color: 'red', description: 'Caps globales de portafolio y por usuario.' },
    ],
  },
  sections: [
    {
      badge: 'Como leer',
      badgeColor: 'blue',
      title: 'Cada tab es una fase del pipeline',
      description: 'Las 5 tabs se ejecutan en orden cuando el motor evalua un usuario. Si una falla, el flujo se detiene.',
      items: [
        {
          label: 'Elegibilidad', icon: 'check',
          description: 'Filtro pasa/no-pasa: edad, ingreso, antiguedad. Si falla, el usuario se rechaza sin llegar a las siguientes fases.',
        },
        {
          label: 'Scoring', icon: 'check',
          description: 'Calcula score 0-100 ponderando 5 dimensiones (ingresos, capacidad de pago, transferencias, antiguedad, nivel de ingresos).',
        },
        {
          label: 'Niveles de Credito', icon: 'check',
          description: 'Asigna nivel segun rango de score. El nivel determina monto maximo y plazo disponible.',
        },
        {
          label: 'Tasas de Interes', icon: 'check',
          description: 'Tasa de interes anualizada por nivel + comision de apertura. Bronze paga mas que Platinum.',
        },
        {
          label: 'Limites de Exposicion', icon: 'check',
          description: 'Caps que evitan concentracion de riesgo: maximo total del portafolio, maximo por usuario, maxima morosidad aceptable.',
        },
      ],
    },
    {
      badge: 'Edicion',
      badgeColor: 'amber',
      title: 'Quien puede editar',
      description: 'Los cambios requieren permiso `CREDIT_POLICIES_EDIT` (configurable por rol). Sin permiso, los formularios estan en modo solo-lectura.',
      items: [
        {
          label: 'Auditoria automatica', icon: 'check',
          description: 'Cada cambio crea un snapshot versionado con quien edito, cuando, y los valores antes/despues. Se ve en Configuracion / Historial.',
        },
        {
          label: 'Sin retroactividad', icon: 'warning',
          description: 'Los cambios aplican SOLO a evaluaciones futuras. Un credito ya desembolsado mantiene las condiciones con las que se origino.',
        },
      ],
    },
  ],
  terms: [
    { term: 'Pipeline', definition: 'Secuencia ordenada de filtros y calculos que determina si un usuario obtiene credito y bajo que condiciones.' },
    { term: 'Snapshot', definition: 'Foto inmutable de la configuracion en un momento dado. Se crea cada vez que se guarda un cambio. Permite auditar y revertir.' },
    { term: 'Knockout Rule', definition: 'Regla eliminatoria: si falla, descalifica al usuario inmediatamente sin evaluar las demas.' },
    { term: 'Sin retroactividad', definition: 'Principio del motor: cambios de politica NO afectan creditos existentes. Solo aplican a creditos NUEVOS desde el cambio.' },
  ],
  faq: [
    { question: '¿Que pasa si cambio una regla?', answer: 'Aplica solo a evaluaciones FUTURAS. Creditos ya activos mantienen su tasa, plazo y nivel originales. Los proximos clientes evaluados usan la nueva configuracion.' },
    { question: '¿Como reverso un cambio?', answer: 'Por ahora no hay rollback automatico. Tienes que ir a Historial, ver el snapshot anterior, y re-capturar los valores manualmente.' },
    { question: '¿Por que Split y Definiciones operativas viven en otra parte?', answer: 'Split (distribucion FiadoSA/UNE-Credito) y parametros operativos (CLABEs, ventana de ingresos, etc.) no son politicas crediticias — son configuracion operativa. Viven en Operaciones / Definiciones.' },
    { question: '¿Que tan seguido cambian estas politicas?', answer: 'Idealmente bajo. Cambiar politicas frecuentemente dificulta medir su impacto. Recomendado: ciclos de revision trimestral basados en data del portafolio.' },
  ],
};

export const POLICIES_ELIGIBILITY_HELP: PageHelpContent = {
  pageKey: 'policies-eligibility',
  title: 'Politicas / Elegibilidad',
  description:
    'Filtro inicial del motor de credito. Define los criterios MINIMOS que un usuario debe cumplir para SER EVALUADO. Si falla cualquier regla aqui, el usuario se rechaza sin pasar a Scoring.',
  capabilities: [
    'Configurar edad minima/maxima',
    'Establecer ingreso mensual minimo en USD',
    'Definir ratio maximo deuda/ingreso',
    'Setear antiguedad minima de la cuenta',
    'Activar/desactivar reglas knockout',
  ],
  sections: [
    {
      badge: 'Concepto',
      badgeColor: 'blue',
      title: 'Pasa o no pasa',
      description: 'Elegibilidad es BINARIA. No suma puntos — solo dice si el usuario tiene derecho a ser evaluado.',
      items: [
        {
          label: 'Si cumple TODAS las reglas', icon: 'check',
          description: 'Continua al siguiente paso (Scoring). El score se calculara con las dimensiones definidas en ese tab.',
        },
        {
          label: 'Si falla CUALQUIERA', icon: 'error',
          description: 'Rechazo inmediato. No se calcula score ni se ofrece nada. El cliente ve mensaje "no elegible" sin detalle del motivo.',
        },
      ],
    },
    {
      badge: 'Reglas tipicas',
      badgeColor: 'cyan',
      title: 'Que se configura aqui',
      description: 'Las reglas mas comunes en este tab:',
      items: [
        { label: 'Edad', icon: 'check', description: 'Minima (18-21 normalmente) y maxima (60-70). Acota el universo legal y demografico.' },
        { label: 'Ingreso minimo', icon: 'check', description: 'En USD. Filtra usuarios sin capacidad de pago demostrada. Configurable por mercado.' },
        { label: 'Antiguedad de cuenta', icon: 'check', description: 'Dias minimos desde que el usuario se registro en App Fiado. Evita usuarios fraudulentos recien creados.' },
        { label: 'Ratio deuda/ingreso', icon: 'warning', description: 'Si el cliente ya carga deuda alta, no puede sumar mas. Ratio comun: 40-50%.' },
      ],
    },
    {
      badge: 'Impacto',
      badgeColor: 'red',
      title: 'Cuidado al modificar',
      description: 'Bajar los thresholds aumenta volumen de credito pero tambien aumenta default. Subirlos hace lo opuesto.',
      items: [
        {
          label: 'Bajar minimos = mas volumen, mas riesgo', icon: 'warning',
          description: 'Mas usuarios pasan al pipeline → mas creditos otorgados → mas defaults esperados.',
        },
        {
          label: 'Subir minimos = menos volumen, menos riesgo', icon: 'check',
          description: 'Filtro mas estricto → menos creditos → menor default rate. Util si la mora supera el target.',
        },
      ],
    },
  ],
  tooltips: [
    { key: 'minAge', tooltip: 'Edad minima para ser elegible (ej. 18 o 21).' },
    { key: 'maxAge', tooltip: 'Edad maxima para ser elegible (ej. 65 o 70).' },
    { key: 'minMonthlyIncome', tooltip: 'Ingreso mensual minimo en USD requerido.' },
    { key: 'maxDebtToIncomeRatio', tooltip: 'Ratio maximo deuda/ingreso aceptable. Mayor = peor.' },
    { key: 'minAccountAgeDays', tooltip: 'Dias minimos desde registro en App Fiado.' },
    { key: 'knockoutRules', tooltip: 'Reglas eliminatorias: si falla alguna, se rechaza sin evaluar las demas.' },
  ],
  terms: [
    { term: 'Knockout', definition: 'Regla eliminatoria. No se balancea con otras — basta con fallar una para rechazar.' },
    { term: 'Universo elegible', definition: 'Conjunto de usuarios que cumplen TODAS las reglas. Es el pool al que se le calcula score.' },
    { term: 'Filtro vs Score', definition: 'Filtro es binario (pasa/no). Score es continuo (0-100). Elegibilidad es filtro; Scoring es score.' },
  ],
  faq: [
    { question: '¿Que pasa con un usuario que ya tiene credito y deja de ser elegible?', answer: 'Mantiene su credito activo. Las reglas de elegibilidad solo aplican al SOLICITAR un credito nuevo. Si renueva o pide otro y ya no califica, se rechaza el nuevo — el activo sigue.' },
    { question: '¿Como sabe el cliente por que fue rechazado?', answer: 'No se le dice cual regla fallo (politica de no exposicion). El cliente solo ve "no eres elegible en este momento". Internamente queda registrado para analisis.' },
    { question: '¿Estos thresholds son por nivel?', answer: 'No. Elegibilidad es global — los mismos criterios aplican a todos los usuarios. Los niveles entran despues, en Scoring/Niveles.' },
  ],
};

export const POLICIES_SCORING_HELP: PageHelpContent = {
  pageKey: 'policies-scoring',
  title: 'Politicas / Scoring',
  description:
    'Configura los pesos de las 5 dimensiones que componen el SCI (Score Crediticio Interno). El score va de 0 a 100. La suma de pesos debe dar 100%.',
  capabilities: [
    'Ajustar el peso de cada una de las 5 dimensiones del score',
    'Validacion automatica: la suma debe sumar 100%',
    'Cada dimension tiene su propia funcion de calculo (definida en backend)',
    'El score resultante determina el nivel del cliente (Bronze/Silver/Gold/Platinum)',
  ],
  sections: [
    {
      badge: 'Las 5 dimensiones',
      badgeColor: 'cyan',
      title: 'Que pondera el score',
      description: 'Cada dimension mide un aspecto distinto de la salud financiera del cliente. Los pesos default estan calibrados — modificar requiere data de respaldo.',
      items: [
        { label: 'Estabilidad de Ingresos (30%)', icon: 'check', description: 'Que tan consistente es el flujo de ingresos del cliente mes a mes. Alta estabilidad = mejor score.' },
        { label: 'Capacidad de Pago (25%)', icon: 'check', description: 'Cuanto del ingreso queda libre despues de gastos. Mayor margen = mejor score.' },
        { label: 'Transferencias a Mexico (20%)', icon: 'check', description: 'Si el cliente envia dinero a wallet MXN regularmente. Senal de uso del producto.' },
        { label: 'Antiguedad (15%)', icon: 'check', description: 'Cuanto tiempo lleva el cliente en la plataforma. Mas tiempo = mas data = mejor score.' },
        { label: 'Nivel de Ingresos (10%)', icon: 'check', description: 'Magnitud absoluta del ingreso (no solo estabilidad). Pondera menos porque la estabilidad importa mas.' },
      ],
    },
    {
      badge: 'Validacion',
      badgeColor: 'amber',
      title: 'La suma debe ser 100%',
      description: 'El sistema NO permite guardar si los pesos no suman 100%. Es validacion estricta.',
      items: [
        {
          label: 'Calculo del score', icon: 'check',
          description: 'score_total = Σ (peso_i × score_i) / 100. Cada sub-score viene de su propia funcion en backend (no se configura aqui).',
        },
      ],
    },
    {
      badge: 'Impacto',
      badgeColor: 'purple',
      title: 'Que cambia si modifico pesos',
      description: 'Subir el peso de una dimension hace al score mas SENSIBLE a esa dimension. Si subes "Antiguedad" al 30%, los clientes nuevos van a salir mucho peor en el score.',
      items: [
        {
          label: 'No es retroactivo (en parte)', icon: 'warning',
          description: 'El nivel y tasa con los que se origino un credito quedan fijos. Pero el score del usuario SI se recalcula al cambiar pesos — afecta su elegibilidad para creditos NUEVOS.',
        },
      ],
    },
  ],
  tooltips: [
    { key: 'weight_income_stability', tooltip: 'Peso de la dimension Estabilidad de Ingresos. Default 30%.' },
    { key: 'weight_payment_capacity', tooltip: 'Peso de la dimension Capacidad de Pago. Default 25%.' },
    { key: 'weight_mexico_transfers', tooltip: 'Peso de la dimension Transferencias a Mexico. Default 20%.' },
    { key: 'weight_seniority', tooltip: 'Peso de la dimension Antiguedad. Default 15%.' },
    { key: 'weight_income_level', tooltip: 'Peso de la dimension Nivel de Ingresos. Default 10%.' },
  ],
  terms: [
    { term: 'SCI', definition: 'Score Crediticio Interno. Puntaje 0-100 calculado por nuestro motor (no es Buro/Circulo).' },
    { term: 'Dimension', definition: 'Variable que se mide y se promedia ponderado para llegar al score total. Hay 5.' },
    { term: 'Peso', definition: 'Porcentaje que aporta una dimension al score total. Configurable aqui.' },
    { term: 'Sub-score', definition: 'Score 0-100 de una dimension individual antes de aplicar su peso.' },
  ],
  faq: [
    { question: '¿Por que no puedo configurar la formula de cada sub-score?', answer: 'Esas formulas viven en codigo backend (no son data). Cambiarlas requiere deploy. Aqui solo configuras como se PONDERAN entre si.' },
    { question: '¿Que score es bueno?', answer: '>=81 es Platinum/Gold (excelente), 61-80 es Silver (bueno), 21-60 es Bronze (basico), <21 no califica.' },
    { question: '¿Puedo desactivar una dimension?', answer: 'Si, poniendo su peso en 0. Pero las demas tienen que ajustarse para sumar 100%. No es buena practica desactivar — perdemos senal.' },
  ],
};

export const POLICIES_CREDIT_LEVELS_HELP: PageHelpContent = {
  pageKey: 'policies-credit-levels',
  title: 'Politicas / Niveles de Credito',
  description:
    'Define los 4 niveles crediticios (Bronze, Silver, Gold, Platinum), sus rangos de score y los limites de monto/plazo de cada uno. Cada nivel es un "tier" con condiciones distintas.',
  capabilities: [
    'Configurar el rango de score que define cada nivel (ej. Bronze 21-60)',
    'Establecer monto minimo y maximo por nivel',
    'Definir plazo minimo y maximo por nivel (en semanas)',
    'Ver y editar las 4 configuraciones simultaneamente',
  ],
  sections: [
    {
      badge: 'Concepto',
      badgeColor: 'purple',
      title: 'Niveles = tiers de cliente',
      description: 'Cada cliente cae en un nivel segun su SCI. El nivel determina cuanto puede pedir prestado y a que plazo.',
      items: [
        { label: 'Bronze (21-60)', icon: 'check', description: 'Nivel basico. Montos bajos ($500-$2,000), plazos cortos (4-8 semanas). Punto de entrada.' },
        { label: 'Silver (61-80)', icon: 'check', description: 'Cliente solido. Montos medios ($2,000-$5,000), plazos medios (4-12 semanas).' },
        { label: 'Gold (81-95)', icon: 'check', description: 'Cliente prime. Montos altos ($5,000-$10,000), plazos largos (4-16 semanas).' },
        { label: 'Platinum (96-100)', icon: 'check', description: 'Cliente top. Mejores condiciones absolutas. Limites mas permisivos.' },
      ],
    },
    {
      badge: 'Rangos',
      badgeColor: 'blue',
      title: 'Score → Nivel',
      description: 'Los rangos son contiguos y cubren 21-100 (debajo de 21 = no elegible). No deben solaparse ni dejar huecos.',
      items: [
        {
          label: 'Validacion', icon: 'check',
          description: 'El sistema valida que los rangos sean contiguos. Score 60 cae en Bronze, 61 en Silver — no hay ambiguedad.',
        },
      ],
    },
    {
      badge: 'Limites',
      badgeColor: 'amber',
      title: 'Monto y plazo por nivel',
      description: 'Cada nivel define su propia ventana. El cliente elige dentro de esa ventana al solicitar.',
      items: [
        {
          label: 'Monto', icon: 'check',
          description: 'Min y max en MXN. Bronze tipicamente $500-$2,000, Platinum hasta $10,000+. Configurable por mercado.',
        },
        {
          label: 'Plazo (semanas)', icon: 'check',
          description: 'Min y max en semanas. Plazos mas largos generalmente solo para niveles altos (mas confianza).',
        },
      ],
    },
  ],
  tooltips: [
    { key: 'level_score_min', tooltip: 'Score minimo (inclusivo) que asigna a este nivel.' },
    { key: 'level_score_max', tooltip: 'Score maximo (inclusivo) que asigna a este nivel.' },
    { key: 'level_amount_min_mxn', tooltip: 'Monto minimo que un cliente de este nivel puede solicitar.' },
    { key: 'level_amount_max_mxn', tooltip: 'Monto maximo que un cliente de este nivel puede solicitar.' },
    { key: 'level_term_min_weeks', tooltip: 'Plazo minimo en semanas disponible para este nivel.' },
    { key: 'level_term_max_weeks', tooltip: 'Plazo maximo en semanas disponible para este nivel.' },
  ],
  terms: [
    { term: 'Nivel', definition: 'Categoria de cliente segun su score. Determina monto y plazo disponibles.' },
    { term: 'Tier', definition: 'Sinonimo de nivel. Termino usado cuando se discute el modelo comercial.' },
    { term: 'Promocion / Democion', definition: 'Subir o bajar de nivel cuando el score cambia. Sucede automaticamente al recalcular score.' },
  ],
  faq: [
    { question: '¿Que pasa si subo a un cliente de Bronze a Silver mientras tiene credito activo?', answer: 'El credito activo mantiene sus condiciones originales. El cambio aplica a su PROXIMA solicitud — ahi vera condiciones de Silver.' },
    { question: '¿Puedo crear un quinto nivel?', answer: 'No desde aqui. Los niveles son fijos en el codigo (BRONZE, SILVER, GOLD, PLATINUM). Agregar uno nuevo requiere desarrollo.' },
    { question: '¿Que pasa si dejo huecos entre rangos?', answer: 'El sistema rechaza guardar. Los 4 rangos deben cubrir 21-100 sin huecos ni solapes.' },
  ],
};

export const POLICIES_INTEREST_RATES_HELP: PageHelpContent = {
  pageKey: 'policies-interest-rates',
  title: 'Politicas / Tasas de Interes',
  description:
    'Define la tasa de interes anualizada y las comisiones por nivel. Bronze paga la tasa mas alta (mayor riesgo), Platinum la mas baja. La tasa se aplica sobre el monto financiado.',
  capabilities: [
    'Configurar tasa anual por nivel (ej. Bronze 260%, Silver 230%, Gold 200%, Platinum 180%)',
    'Definir comision de apertura (porcentaje sobre el monto solicitado)',
    'Establecer si la comision se cobra al desembolso o se financia',
    'Configurar tasa de IVA aplicable a la comision',
  ],
  sections: [
    {
      badge: 'Tasa por nivel',
      badgeColor: 'amber',
      title: 'Pricing por riesgo',
      description: 'La tasa anualizada es el componente principal del costo. Se aplica al saldo de capital semana a semana.',
      items: [
        { label: 'Bronze ~260%', icon: 'warning', description: 'Mayor tasa porque mayor riesgo de default. Tipica para nivel inicial.' },
        { label: 'Silver ~230%', icon: 'check', description: 'Menor riesgo, menor tasa. Recompensa al cliente que sube de nivel.' },
        { label: 'Gold ~200%', icon: 'check', description: 'Cliente prime — tasa competitiva.' },
        { label: 'Platinum ~180%', icon: 'check', description: 'Mejor tasa disponible. Reservada al top del portafolio.' },
      ],
    },
    {
      badge: 'Comision',
      badgeColor: 'blue',
      title: 'Comision de apertura',
      description: 'Porcentaje cobrado UNA SOLA VEZ al desembolsar el credito. Adicional al interes corriente.',
      items: [
        { label: 'Tasa actual', icon: 'check', description: 'Configurada actualmente en 0% (estructura lista para activarse cuando el negocio lo decida).' },
        { label: 'Cobro al desembolso', icon: 'check', description: 'Si esta activa: se descuenta del monto desembolsado o se suma al financiado (configurable).' },
        { label: 'IVA sobre comision', icon: 'check', description: '16% sobre la comision (no sobre el capital). Lo retiene FiadoSA.' },
      ],
    },
    {
      badge: 'CAT',
      badgeColor: 'cyan',
      title: 'Que se reporta al cliente',
      description: 'Por regulacion mexicana se reporta el CAT (Costo Anual Total) que incluye tasa + comision + IVA. El cliente ve un solo numero.',
      items: [
        {
          label: 'Formula CAT', icon: 'check',
          description: 'CAT = (1 + tasa_efectiva + comision_amortizada + IVA) ^ 12 − 1. Incluye todo en una sola tasa comparable.',
        },
        {
          label: 'Cuidado con los cambios', icon: 'warning',
          description: 'Modificar tasa o comision cambia el CAT — debe re-aprobarse legalmente y actualizar contratos modelo si cambian rangos significativamente.',
        },
      ],
    },
  ],
  tooltips: [
    { key: 'rate_bronze_annual', tooltip: 'Tasa anualizada para nivel Bronze.' },
    { key: 'rate_silver_annual', tooltip: 'Tasa anualizada para nivel Silver.' },
    { key: 'rate_gold_annual', tooltip: 'Tasa anualizada para nivel Gold.' },
    { key: 'rate_platinum_annual', tooltip: 'Tasa anualizada para nivel Platinum.' },
    { key: 'opening_fee_pct', tooltip: 'Comision de apertura cobrada al desembolso (porcentaje sobre monto solicitado).' },
    { key: 'opening_fee_iva_rate', tooltip: 'Tasa de IVA aplicable sobre la comision (default 16%).' },
    { key: 'opening_fee_financed', tooltip: 'Si true, la comision se suma al monto financiado. Si false, se descuenta del desembolso.' },
  ],
  terms: [
    { term: 'TNA', definition: 'Tasa Nominal Anual. La tasa que ves aqui es TNA — antes de capitalizar.' },
    { term: 'CAT', definition: 'Costo Anual Total. Incluye TNA + comisiones + IVA. Es lo que se reporta legalmente al cliente.' },
    { term: 'Comision de apertura', definition: 'Cargo unico al desembolsar. No es interes — es por servicio de originacion.' },
    { term: 'Tasa efectiva semanal', definition: 'TNA / 52. La tasa que se aplica al saldo semana a semana.' },
  ],
  faq: [
    { question: '¿Puedo cambiar la tasa de un credito existente?', answer: 'No. La tasa con la que se origino queda fija hasta liquidar. Cambios aqui solo afectan creditos NUEVOS.' },
    { question: '¿Por que la comision esta en 0%?', answer: 'Decision comercial actual: simplificar el pricing. La estructura esta lista para activarla cuando el negocio decida cobrarla (sin cambio de codigo).' },
    { question: '¿Donde se configura el split de la tasa entre FiadoSA y UNE-Credito?', answer: 'No aqui. El split vive en Operaciones / Definiciones / Split. Aqui solo se define la tasa TOTAL.' },
  ],
};

export const POLICIES_EXPOSURE_LIMITS_HELP: PageHelpContent = {
  pageKey: 'policies-exposure-limits',
  title: 'Politicas / Limites de Exposicion',
  description:
    'Caps GLOBALES que protegen el portafolio del riesgo de concentracion y default masivo. Son freno de emergencia: si se llega al limite, el motor deja de originar nuevos creditos hasta que el indicador baje.',
  capabilities: [
    'Establecer monto maximo total del portafolio activo',
    'Definir monto maximo por credito individual',
    'Limitar creditos simultaneos por usuario',
    'Setear tasa maxima de morosidad aceptable (kill switch)',
    'Establecer concentracion maxima por usuario (% del portafolio)',
  ],
  sections: [
    {
      badge: 'Concepto',
      badgeColor: 'red',
      title: 'Caps de seguridad',
      description: 'A diferencia de Elegibilidad y Niveles (que aplican por usuario), estos limites son AGREGADOS. Miden salud del portafolio entero.',
      items: [
        {
          label: 'Limite global de portafolio', icon: 'check',
          description: 'Monto maximo total en creditos vivos. Si llegas al cap, NO se originan mas creditos — aunque haya clientes elegibles.',
        },
        {
          label: 'Concentracion por usuario', icon: 'check',
          description: 'Porcentaje maximo del portafolio que puede tener UN solo usuario. Evita "huevos en una canasta".',
        },
        {
          label: 'Tasa maxima de morosidad', icon: 'warning',
          description: 'Si la mora del portafolio rebasa este threshold, el motor pausa originacion. Auto-recovery cuando baja.',
        },
      ],
    },
    {
      badge: 'Por usuario',
      badgeColor: 'blue',
      title: 'Limites individuales',
      description: 'Caps que aplican a cada usuario, no al portafolio agregado:',
      items: [
        {
          label: 'Monto maximo por credito', icon: 'check',
          description: 'Cap absoluto independiente del nivel. Aunque el nivel permita mas, este limite se respeta.',
        },
        {
          label: 'Creditos simultaneos por usuario', icon: 'check',
          description: 'Cuantos creditos vivos puede tener un mismo cliente. Tipicamente 1 (decision actual: un credito a la vez).',
        },
      ],
    },
    {
      badge: 'Kill switch',
      badgeColor: 'red',
      title: 'Tasa de morosidad como freno automatico',
      description: 'Si la tasa de mora rebasa el threshold, el motor automaticamente deja de originar — protege el portafolio sin intervencion humana.',
      items: [
        {
          label: 'Activacion automatica', icon: 'warning',
          description: 'No requiere accion. El sistema chequea la mora periodicamente y pausa si excede.',
        },
        {
          label: 'Reactivacion automatica', icon: 'check',
          description: 'Cuando la mora baja del threshold (por liquidaciones o cobros), el motor reanuda. Sin necesidad de "destrabar" manualmente.',
        },
      ],
    },
  ],
  tooltips: [
    { key: 'maxTotalExposure', tooltip: 'Monto maximo del portafolio activo (capital vivo). Si se alcanza, no se originan mas.' },
    { key: 'maxSingleCreditAmount', tooltip: 'Monto maximo absoluto por credito individual.' },
    { key: 'maxCreditsPerUser', tooltip: 'Creditos vivos simultaneos maximos por usuario.' },
    { key: 'maxPortfolioDelinquencyRate', tooltip: 'Tasa de mora maxima del portafolio. Si se rebasa, pausa originacion.' },
    { key: 'concentrationLimitPercent', tooltip: 'Porcentaje maximo del portafolio total que puede tener un solo usuario.' },
  ],
  terms: [
    { term: 'Exposicion', definition: 'Monto total en riesgo. Suma de saldos vivos del portafolio.' },
    { term: 'Concentracion', definition: 'Porcentaje del portafolio asignado a un solo cliente. Alta concentracion = alto riesgo si ese cliente defaultea.' },
    { term: 'Kill switch', definition: 'Mecanismo automatico que pausa originacion si un indicador rebasa su threshold. No requiere intervencion humana.' },
    { term: 'Auto-recovery', definition: 'Reactivacion automatica cuando el indicador vuelve a niveles seguros.' },
  ],
  faq: [
    { question: '¿Que pasa si cambio el limite total del portafolio cuando ya esta excedido?', answer: 'No afecta creditos vivos. Pero NO se originaran nuevos hasta que el portafolio baje del nuevo limite (por liquidaciones).' },
    { question: '¿La tasa maxima de morosidad es estricta?', answer: 'Si. Es kill switch. Excederla pausa originacion automaticamente. Es el mecanismo de auto-proteccion mas critico del motor.' },
    { question: '¿Por que limitar creditos por usuario?', answer: 'Para evitar "bicicletas crediticias" (cliente paga uno con otro). Hoy se permite 1 simultaneo. Subir requiere analisis de comportamiento.' },
  ],
};

export const POLICIES_WHITELIST_HELP: PageHelpContent = {
  pageKey: 'policies-whitelist',
  title: 'Politicas / Whitelist',
  description:
    'Lista de excepcion para pruebas productivas: los clientes que se agregan aqui reciben oferta al nivel GOLD saltando la evaluacion normal del motor (elegibilidad, score y meses en plataforma). Solo usarse con clientes que hayan firmado consentimiento para pruebas.',
  capabilities: [
    'Agregar clientes existentes buscando por nombre o DirectoryId',
    'Ver la lista de clientes activos en la whitelist (cap 20 por partner)',
    'Quitar clientes (soft delete: quedan en historico auditable)',
    'Consultar el historico de removidos con quien los quito y cuando',
  ],
  sections: [
    {
      badge: 'Como opera',
      badgeColor: 'blue',
      title: 'Bypass controlado del motor',
      description: 'Cuando el motor evalua un cliente que esta en esta lista, salta Elegibilidad y Score y le asigna GOLD directo. El resto del calculo (monto maximo segun ingreso, tasas, plazos) se mantiene igual que un GOLD normal.',
      items: [
        {
          label: 'Cliente en la lista', icon: 'check',
          description: 'Recibe oferta como GOLD sin importar su score real. El banner del cliente pasa a ELIGIBLE en segundos gracias al backfill sincrono.',
        },
        {
          label: 'Cliente quitado', icon: 'warning',
          description: 'Vuelve a evaluacion normal en la proxima corrida del motor (se recomputa profile inmediatamente por el backfill).',
        },
        {
          label: 'Cliente con credito activo', icon: 'error',
          description: 'La whitelist NO reemplaza el hard gate has_active_credit. Aunque este en la lista, no recibe nueva oferta hasta cerrar el credito actual.',
        },
      ],
    },
    {
      badge: 'Feature flag',
      badgeColor: 'red',
      title: 'Solo aplica si WHITELIST_ENABLED esta activo',
      description: 'El comportamiento GOLD ficticio solo se aplica cuando el feature flag WHITELIST_ENABLED del backend esta prendido. Si esta apagado, agregar clientes aqui no tiene efecto sobre el motor (los endpoints CRUD siguen respondiendo, pero el override no ejecuta).',
      items: [
        {
          label: 'Endpoints independientes del flag', icon: 'check',
          description: 'Se pueden agregar/quitar/listar clientes aunque el flag este apagado. Util para preparar la lista antes de encender el flag.',
        },
        {
          label: 'Motor solo honra si flag ON', icon: 'warning',
          description: 'El override en CreditLevelService.assignLevel del engine y del worker solo consulta esta tabla si WHITELIST_ENABLED=true. Sino, evaluacion normal.',
        },
      ],
    },
    {
      badge: 'Cuidados',
      badgeColor: 'red',
      title: 'Para que NO usar esta tab',
      description: 'Esta funcionalidad es solo para pruebas productivas controladas. Usos que NO estan pensados:',
      items: [
        { label: 'Otorgar credito por favor', icon: 'error', description: 'No es una vuelta para saltar la evaluacion crediticia en produccion regular. Cada cliente aqui debe tener consentimiento firmado.' },
        { label: 'Marketing masivo', icon: 'error', description: 'El cap de 20 clientes activos por partner esta pensado para experimentos acotados, no para lanzar campanas.' },
        { label: 'Suplir score bajo', icon: 'error', description: 'Si un cliente falla el score sistematicamente, no lo agregues aqui — el score refleja riesgo real y saltarlo genera perdidas.' },
      ],
    },
  ],
  tooltips: [
    { key: 'displayName', tooltip: 'Nombre completo del cliente en el momento de agregarlo. Se muestra en la tabla y en historico auditable.' },
    { key: 'creditLevel', tooltip: 'Nivel forzado. En esta iteracion siempre es GOLD; el schema soporta otros niveles para pruebas futuras.' },
    { key: 'addedBy', tooltip: 'Email del operador que agrego al cliente. Se llena automatico desde el usuario logueado.' },
  ],
  terms: [
    { term: 'Backfill sincrono', definition: 'Al agregar/quitar un cliente, se dispara inmediatamente el recomputo de su profile y banner en el worker. Sin este backfill, tendrias que esperar al P2 semanal para ver el cambio.' },
    { term: 'Soft delete', definition: 'Quitar un cliente lo marca is_active=NO en vez de borrarlo. Queda en historico con quien y cuando lo removio.' },
    { term: 'has_active_credit', definition: 'Flag del profile que indica si el cliente ya tiene un credito ACTIVE, DELINQUENT o DEFAULTED. Bloquea nueva oferta aunque este en whitelist.' },
    { term: 'Feature flag WHITELIST_ENABLED', definition: 'Parametro SSM /fiado/credit/credit-whitelist-enabled. Si es false, la whitelist se ignora en el motor (los endpoints siguen respondiendo pero no tiene efecto).' },
  ],
  faq: [
    { question: '¿Que pasa si agrego a un cliente que no cumple ingreso minimo?', answer: 'El motor le asigna GOLD y calcula monto maximo con su ingreso conocido. Si su ingreso reportado es cero, el monto queda en cero — el cap de GOLD no lo eleva magicamente.' },
    { question: '¿Puedo distinguir en el resto del Dashboard cuales GOLD son de whitelist?', answer: 'Aun no. El profile solo persiste credit_level=GOLD sin marca de "vino del whitelist" (deuda en asana). Consultar esta tab es la forma actual de verificar.' },
    { question: '¿Cuanto tarda en verse la oferta despues de agregar?', answer: 'Segundos. El POST dispara SQS al worker que recompute profile + banner. El toast te dice si el backfill fue OK y cuantos ms tardo.' },
    { question: '¿El cap de 20 es configurable?', answer: 'No en esta iteracion. Es una constante en el WhitelistManager del engine. Subirlo requiere cambio de codigo.' },
  ],
};

export const CONFIG_HISTORY_HELP: PageHelpContent = {
  pageKey: 'config-history',
  title: 'Historial de Configuracion',
  description:
    'Bitacora versionada de TODOS los cambios a politicas (Configuracion / Politicas) y a definiciones operativas (Operaciones / Definiciones). Cada vez que se guarda un cambio, se crea un snapshot inmutable con quien, cuando y los valores antes/despues.',
  capabilities: [
    'Ver la lista de snapshots ordenados por fecha descendente',
    'Filtrar por tipo de configuracion (split, bank-accounts, operational-params, etc.)',
    'Click en un snapshot para ver el detalle de los valores',
    'Comparar snapshots consecutivos para entender que cambio',
    'Identificar quien hizo cada cambio (auditoria)',
  ],
  sections: [
    {
      badge: 'Para que sirve',
      badgeColor: 'blue',
      title: 'Auditoria + capacidad de revertir',
      description: 'Es el "git log" de la configuracion del motor. Sin este historial, perderias trazabilidad de cambios y no podrias entender por que el motor se comporta distinto en distintas fechas.',
      items: [
        {
          label: 'Auditoria regulatoria', icon: 'check',
          description: 'Para reportes regulatorios necesitamos demostrar las reglas que aplicaban en cualquier momento del tiempo. Este historial es la fuente de verdad.',
        },
        {
          label: 'Diagnostico de incidentes', icon: 'check',
          description: 'Si un comportamiento del motor cambia inesperadamente, primero se mira aqui — alguien tocco la config?',
        },
        {
          label: 'Recuperacion manual', icon: 'warning',
          description: 'Si necesitas revertir, ves los valores anteriores aqui y los re-capturas en la pantalla de edicion. No hay rollback automatico (todavia).',
        },
      ],
    },
    {
      badge: 'Tipos de config',
      badgeColor: 'purple',
      title: 'Que se trackea',
      description: 'Esta misma pantalla muestra dos universos segun como se navega:',
      items: [
        {
          label: 'Desde Configuracion / Politicas', icon: 'check',
          description: 'Snapshots de cambios a politicas crediticias: elegibilidad, scoring, niveles, tasas, limites.',
        },
        {
          label: 'Desde Operaciones / Definiciones', icon: 'check',
          description: 'Snapshots de definiciones operativas: split FiadoSA/UNE-Credito, CLABEs bancarias, parametros de cobranza, ventana de ingresos, etc.',
        },
        {
          label: 'Filtro por configType', icon: 'check',
          description: 'La URL acepta `?configType=split,bank-accounts` para filtrar. La navegacion desde cada modulo aplica el filtro automaticamente.',
        },
      ],
    },
    {
      badge: 'Lectura de snapshot',
      badgeColor: 'cyan',
      title: 'Que ves al abrir uno',
      description: 'Cada snapshot incluye:',
      items: [
        { label: 'Fecha y hora', icon: 'check', description: 'Cuando se guardo el cambio (timestamp UTC).' },
        { label: 'Usuario', icon: 'check', description: 'Email del operador que ejecuto el cambio.' },
        { label: 'Valores capturados', icon: 'check', description: 'La configuracion completa al momento del save (no solo el delta).' },
        { label: 'Tipo (configType)', icon: 'check', description: 'A que area pertenece (split, bank-accounts, eligibility, scores, etc.).' },
      ],
    },
  ],
  terms: [
    { term: 'Snapshot', definition: 'Foto inmutable de la configuracion completa de un area en un momento dado.' },
    { term: 'configType', definition: 'Identificador del area de configuracion (ej. split, bank-accounts, eligibility). Permite filtrar el historial.' },
    { term: 'Delta', definition: 'Diferencia entre dos snapshots consecutivos. Se calcula visualmente comparando — no se guarda explicitamente.' },
    { term: 'Inmutable', definition: 'No se puede editar ni borrar un snapshot. Es la garantia de auditoria — lo que paso, paso.' },
  ],
  faq: [
    { question: '¿Puedo borrar snapshots viejos?', answer: 'No. Son inmutables por diseño. Si la lista crece mucho, se puede paginar — pero no eliminar.' },
    { question: '¿Hay rollback automatico?', answer: 'No por ahora. Si quieres revertir, abres el snapshot anterior, copias los valores, y los re-capturas en la pantalla de edicion correspondiente.' },
    { question: '¿Por que dos titulos posibles ("Historial de Politicas" / "Historial de Definiciones")?', answer: 'Es la misma pantalla pero con titulo dinamico segun de donde llegaste. Operaciones / Definiciones → Historial de Definiciones. Configuracion / Politicas → Historial de Politicas.' },
    { question: '¿Que pasa si un snapshot tiene formato distinto (campos antiguos)?', answer: 'Se renderea con lo que tenga. Si la shape cambio (ej. agregamos un campo nuevo), los snapshots viejos no lo tienen — es esperado.' },
  ],
};

export const SIMULATOR_HELP: PageHelpContent = {
  pageKey: 'simulator',
  title: 'Simulador de Credito',
  description:
    'Herramienta para simular creditos con parametros personalizados sin afectar datos reales.',
  capabilities: [
    'Simular un credito con monto y plazo personalizado',
    'Especificar tasa de interes o usar la del nivel',
    'Ver pago mensual, total a pagar e intereses',
    'Obtener tabla de amortizacion detallada',
    'Hacer multiples simulaciones',
  ],
  tooltips: [
    {
      key: 'amount',
      tooltip: 'Monto del credito a simular (500 - 50,000 MXN)',
    },
    {
      key: 'termDays',
      tooltip: 'Plazo en dias. Multiplos de 7 (semanas)',
    },
    {
      key: 'interestRate',
      tooltip: 'Tasa anual. Si vacio, usa la del nivel.',
    },
    {
      key: 'level',
      tooltip:
        'Nivel crediticio. Determina la tasa si no se especifica una.',
    },
    {
      key: 'monthlyPayment',
      tooltip: 'Pago mensual estimado',
    },
    {
      key: 'totalPayment',
      tooltip: 'Monto total a pagar (capital + intereses)',
    },
    {
      key: 'totalInterest',
      tooltip: 'Total de intereses generados',
    },
  ],
  faq: [
    {
      question: 'La simulacion afecta datos reales?',
      answer: 'No. Es completamente independiente.',
    },
    {
      question: 'Porque la tasa es diferente?',
      answer:
        'Si especificas una tasa manual, se usa esa. Si no, se usa la del nivel seleccionado.',
    },
  ],
};

// ─── Portfolio ───────────────────────────────────────────────────────────────

export const PORTFOLIO_DASHBOARD_HELP: PageHelpContent = {
  pageKey: 'portfolio-dashboard',
  title: 'Portafolio Actual',
  description:
    'Foto del portafolio HOY. Combina KPIs principales, mora del momento y las ultimas transferencias semanales a UNE-Credito. Para ver evolucion en el tiempo, usar Portafolio Historico.',
  capabilities: [
    'Ver creditos activos, monto en cartera y tasa de morosidad de un vistazo',
    'Detectar si la mora supera el umbral aceptable (icono rojo si >10%)',
    'Revisar las ultimas 5 transferencias enviadas a UNE-Credito',
    'Saltar al detalle de morosos, defaults o lista completa de creditos',
  ],
  sections: [
    {
      badge: 'KPIs',
      badgeColor: 'blue',
      title: 'Tres metricas principales',
      description: 'Las tres tarjetas de arriba responden las preguntas mas urgentes sobre el portafolio.',
      items: [
        {
          label: 'Creditos Activos', icon: 'check',
          description: 'Numero de creditos con estado ACTIVE. Excluye morosos, defaults, liquidados y cancelados.',
        },
        {
          label: 'Monto en Cartera', icon: 'check',
          description: 'Suma de los montos originales de creditos activos. El subtitulo muestra el monto promedio por credito.',
        },
        {
          label: 'Tasa de Morosidad', icon: 'warning',
          description: 'Porcentaje de creditos en mora sobre el total. Si pasa de 10% el icono se pone rojo — alerta operativa.',
        },
      ],
    },
    {
      badge: 'Bloque Morosidad',
      badgeColor: 'red',
      title: 'Cuanto y cuantos en mora',
      description: 'Desglose en 3 numeros + barra visual: cuantos creditos morosos hay, cuanto saldo representan y la tasa relativa al total.',
      items: [
        {
          label: 'Click en "Ver detalle"',
          description: 'Lleva a Portafolio Moroso con la lista completa, semanas de atraso y acciones de cobranza.',
        },
      ],
    },
    {
      badge: 'Bloque Transferencias',
      badgeColor: 'green',
      title: 'Ultimas 5 transferencias a UNE-Credito',
      description: 'Resumen de las transferencias mas recientes enviadas via SPEI. Ordenadas por fecha descendente.',
      items: [
        {
          label: 'Click en "Ver todas"',
          description: 'Abre el historial completo de transferencias con sus claves de rastreo y montos desglosados.',
        },
      ],
    },
  ],
  terms: [
    { term: 'Activo', definition: 'Credito vigente y al corriente. No tiene cupones vencidos sin pagar.' },
    { term: 'Moroso', definition: 'Credito con uno o mas pagos vencidos. Entre 1 y 8 semanas de atraso (configurable en Operaciones / Definiciones).' },
    { term: 'Default', definition: 'Credito que excedio el umbral de mora y se marco como baja contable. Por defecto a las 8 semanas de mora consecutiva.' },
    { term: 'Saldo en Mora', definition: 'Suma del saldo pendiente (capital + interes + IVA) de todos los creditos morosos.' },
    { term: 'Transferencia a UNE-Credito', definition: 'Envio SPEI semanal donde FiadoSA traslada los cobros (principal + intereses UNE-Credito) reteniendo su comision (5/60) + IVA.' },
  ],
  statuses: [
    { value: 'COMPLETED', label: 'Completada', description: 'Transferencia procesada y conciliada con el banco.', variant: 'success' },
    { value: 'APPROVED', label: 'Aprobada', description: 'Transferencia aprobada por el operador, pendiente de procesar.', variant: 'success' },
    { value: 'PENDING', label: 'Pendiente', description: 'Transferencia creada pero aun sin procesar.', variant: 'warning' },
  ],
  faq: [
    { question: '¿Cada cuanto se actualizan los datos?', answer: 'Cada vez que abres la pantalla. Usa el boton "Actualizar" para forzar refresh.' },
    { question: '¿Por que el monto en cartera difiere del monto desembolsado historico?', answer: 'El monto en cartera suma SOLO creditos activos hoy. El desembolsado historico incluiria liquidados, defaults y cancelados.' },
    { question: '¿Que pasa si la tasa de morosidad pasa 10%?', answer: 'El icono de la KPI se pone rojo como alerta visual. Operativamente revisar Portafolio Moroso para ver si la concentracion esta en una semana o nivel especifico.' },
    { question: '¿Por que no veo creditos defaulted en esta vista?', answer: 'Default es una vista separada (icono Undo en los nav links abajo). Aqui solo se ven activos + morosos para mantener el foco operativo.' },
  ],
};

export const PORTFOLIO_HISTORICO_HELP: PageHelpContent = {
  pageKey: 'portfolio-historico',
  title: 'Portafolio Historico',
  description:
    '¿Como se ha comportado el portafolio en el tiempo? 6 graficas en 3 bloques: Evolucion (saldo y creditos vivos), Mora (tasa y monto) y Resolucion (pagados / default / recuperacion).',
  capabilities: [
    'Comparar el saldo vivo y la cantidad de creditos vivos mes a mes',
    'Ver la evolucion de la tasa de morosidad y el monto en mora',
    'Analizar como se resuelven los creditos: pagados vs default vs cancelados',
    'Calcular la tasa de recuperacion (% de creditos resueltos exitosamente)',
    'Cambiar entre vista Mensual y Anual con el selector arriba a la derecha',
  ],
  sections: [
    {
      badge: 'Bloque 1',
      badgeColor: 'blue',
      title: 'Evolucion del Portafolio',
      description: 'Como crece (o decrece) el portafolio a lo largo del tiempo. Dos angulos de la misma pregunta: dinero y volumen.',
      items: [
        {
          label: 'Saldo Vivo (barras)', icon: 'check',
          description: 'Suma del saldo pendiente de todos los creditos activos al cierre de cada periodo. Excluye lo ya cobrado y lo defaulteado.',
        },
        {
          label: 'Creditos Vivos (linea)', icon: 'check',
          description: 'Cantidad de creditos en estado ACTIVE al cierre de cada periodo. Refleja la rotacion: nuevos desembolsos vs liquidaciones / defaults.',
        },
      ],
    },
    {
      badge: 'Bloque 2',
      badgeColor: 'red',
      title: 'Evolucion de Morosidad',
      description: 'Salud del portafolio. La tasa de mora y el monto en mora deben monitorearse mes a mes.',
      items: [
        {
          label: 'Tasa de Morosidad (linea roja)', icon: 'warning',
          description: 'Porcentaje de creditos morosos sobre el total activo. Tendencia descendente = portafolio mas sano. Ascendente = revisar scoring o cobranza.',
        },
        {
          label: 'Monto en Mora (barras)', icon: 'warning',
          description: 'Saldo pendiente de los creditos morosos al cierre del periodo. Combinado con la tasa permite distinguir entre "muchos morosos chicos" vs "pocos morosos grandes".',
        },
      ],
    },
    {
      badge: 'Bloque 3',
      badgeColor: 'green',
      title: 'Resolucion de Creditos',
      description: 'Que pasa con los creditos cuando "salen" del portafolio activo. La barra apilada muestra el destino, la linea muestra la efectividad.',
      items: [
        {
          label: 'Resolucion (barras apiladas)', icon: 'check',
          description: 'Por cada periodo: cuantos creditos se liquidaron exitosamente (verde), cuantos cayeron en default (rojo) y cuantos se cancelaron (gris).',
        },
        {
          label: 'Tasa de Recuperacion (linea verde)', icon: 'check',
          description: 'Porcentaje de creditos resueltos que terminaron pagados (vs default + cancelados). Es el indicador final de calidad del portafolio.',
        },
      ],
    },
  ],
  terms: [
    { term: 'Saldo Vivo', definition: 'Saldo pendiente (capital + interes + IVA no cobrado) de todos los creditos activos. Lo que se debe en este momento.' },
    { term: 'Credito Vivo', definition: 'Credito que sigue en estado ACTIVE — ni liquidado ni en default. Aun genera cupones por cobrar.' },
    { term: 'Tasa de Morosidad', definition: 'Creditos morosos / Total activos × 100. Un solo numero que resume la salud del portafolio.' },
    { term: 'Default / Baja Contable', definition: 'Credito que excedio el umbral de mora (8 semanas por defecto) y se da por perdido contablemente. La cobranza pasa a modo latente.' },
    { term: 'Tasa de Recuperacion', definition: 'Pagados / (Pagados + Default + Cancelados) × 100. Indica que porcentaje de los creditos que "salen" lo hacen exitosamente.' },
    { term: 'Mensual vs Anual', definition: 'El selector cambia la granularidad de los puntos en las graficas. Mensual permite ver detalle fino; Anual muestra tendencia de largo plazo.' },
  ],
  faq: [
    { question: '¿Por que el saldo vivo no coincide con el monto en cartera del Panel?', answer: 'El Panel muestra el monto ORIGINAL de los creditos activos. Aqui se muestra el saldo PENDIENTE (lo que todavia se debe). La diferencia es lo ya cobrado.' },
    { question: '¿Por que el ultimo mes a veces se ve raro?', answer: 'El mes en curso esta incompleto: las graficas muestran el corte hasta hoy, no el mes terminado. Se "completara" al cerrar el mes.' },
    { question: '¿Que es una tasa de recuperacion sana?', answer: 'Idealmente >85%. Por debajo de 70% indica problemas serios de scoring o cobranza. Comparar contra benchmarks del sector.' },
    { question: '¿Porque hay cancelados en la grafica de resolucion?', answer: 'Cancelado = el cliente cancelo el credito antes de incumplir (ej. liquido anticipado por venta del bien financiado). No es default ni pago normal — es una tercera categoria.' },
  ],
};

export const PORTFOLIO_ACTIVE_HELP: PageHelpContent = {
  pageKey: 'portfolio-active',
  title: 'Portafolio Activo',
  description: 'Lista detallada de todos los creditos activos con busqueda y paginacion.',
  capabilities: [
    'Buscar creditos por ID',
    'Ver monto, plazo, tasa y pago semanal',
    'Identificar el estado de cada credito',
    'Ordenar por cualquier columna',
  ],
};

export const PORTFOLIO_DELINQUENT_HELP: PageHelpContent = {
  pageKey: 'portfolio-delinquent',
  title: 'Portafolio Moroso',
  description: 'Creditos en estado de mora. Muestra el conteo de morosos, saldo pendiente y tasa de morosidad.',
  capabilities: [
    'Ver total de creditos morosos y saldo vencido',
    'Monitorear la tasa de morosidad del portafolio',
    'Identificar creditos con mas semanas sin pago',
    'Ver la fecha de inicio de mora de cada credito',
  ],
};

export const PORTFOLIO_DEFAULTED_HELP: PageHelpContent = {
  pageKey: 'portfolio-defaulted',
  title: 'Creditos en Default',
  description: 'Creditos en estado de default. Muestra montos originales, pendientes y cobrados.',
  capabilities: [
    'Ver total de creditos defaulteados',
    'Analizar deuda original vs monto cobrado',
    'Identificar saldo pendiente por recuperar',
    'Ver semanas sin pago de cada credito',
  ],
};

// ─── Collection ──────────────────────────────────────────────────────────────

export const COLLECTION_DASHBOARD_HELP: PageHelpContent = {
  pageKey: 'collection-dashboard',
  title: 'Panel de Cobranza',
  description:
    '¿Que hay que cobrar HOY y como va la operacion? KPIs del dia + ultimos 5 dias + tendencia semanal + resumen de cobranza latente. Es el primer punto de revision diaria del equipo de cobranza.',
  capabilities: [
    'Ver lo esperado y cobrado HOY de un vistazo',
    'Identificar cobros parciales y sin cobro que requieren seguimiento',
    'Comparar la cobranza de los ultimos 5 dias para detectar caidas',
    'Monitorear la tasa de cobro semanal vs el target de 93%',
    'Ver el resumen de cobranza latente (creditos en default) sin abandonar la pantalla',
  ],
  sections: [
    {
      badge: 'KPIs del dia',
      badgeColor: 'blue',
      title: 'Las 4 tarjetas de arriba',
      description: 'Resumen del estado de cobranza de HOY al momento de abrir la pantalla.',
      items: [
        {
          label: 'Esperado Hoy', icon: 'check',
          description: 'Suma de los cupones cuya fecha de vencimiento es hoy. No incluye atrasados de dias previos.',
        },
        {
          label: 'Cobrado Hoy', icon: 'check',
          description: 'Suma de cobros recibidos hoy. Puede incluir pagos a cupones de hoy y pagos a cupones vencidos antes (catch-up).',
        },
        {
          label: 'Tasa del Dia', icon: 'check',
          description: 'Cobrado / Esperado × 100. Si es <93% al cierre del dia, hay desviacion vs target operativo.',
        },
        {
          label: 'Cobros Totales Hoy', icon: 'check',
          description: 'Cantidad de cupones cobrados al 100% hoy. El subtitulo desglosa: parciales (cobro incompleto) y sin cobro (no se recibio nada).',
        },
      ],
    },
    {
      badge: 'Cobranza Reciente',
      badgeColor: 'cyan',
      title: 'Tabla de los ultimos 5 dias',
      description: 'Compara dia a dia para detectar caidas. La fila "Hoy" se resalta. La columna "Moroso" es la diferencia Esperado − Cobrado (en rojo si positivo).',
      items: [
        {
          label: 'Cobros Totales',
          description: 'Cuantos cupones se cobraron al 100%. Comparable entre dias incluso si los montos varian.',
        },
        {
          label: 'Parciales / Sin Cobro',
          description: 'Cuantos cupones se cobraron parcialmente y cuantos no se cobraron. En rojo si hay alguno — son los que requieren accion del equipo.',
        },
      ],
    },
    {
      badge: 'Tasa Semanal',
      badgeColor: 'green',
      title: 'Eficiencia de cobranza por semana',
      description: 'Linea azul = tasa de cobro semanal. Linea verde punteada = target operativo (93%). Si la tasa se mantiene por debajo varias semanas seguidas, hay un problema estructural — no anecdotico.',
      items: [],
    },
    {
      badge: 'Latente',
      badgeColor: 'orange',
      title: 'Resumen de cobranza latente',
      description: 'Vista rapida del desempeno de la cartera en default. 3 KPIs y un link al detalle completo en /collection/latent.',
      items: [
        {
          label: 'Creditos Latentes',
          description: 'Total de creditos en estado DEFAULT.',
        },
        {
          label: 'Pendiente',
          description: 'Saldo pendiente de recuperar (deuda original − ya cobrado).',
        },
        {
          label: 'Tasa Recuperacion',
          description: 'Cobrado / Deuda original × 100. Mide cuanto se ha logrado recuperar de los creditos perdidos.',
        },
      ],
    },
  ],
  terms: [
    { term: 'Cobro Parcial', definition: 'El cliente pago una parte del cupon. Quedo saldo pendiente.' },
    { term: 'Sin Cobro', definition: 'No se recibio nada del cupon esperado. Es la categoria mas critica del dia.' },
    { term: 'Catch-up', definition: 'Pago hoy de un cupon que vencio antes. Aumenta "Cobrado Hoy" pero NO el "Esperado Hoy".' },
    { term: 'Tasa de Cobro', definition: 'Cobrado / Esperado en un periodo. Se mide diaria, semanal y mensualmente para distintos analisis.' },
    { term: 'Target 93%', definition: 'Linea verde de referencia en el grafico semanal. Es el threshold operativo definido por el equipo.' },
  ],
  faq: [
    { question: '¿Por que la tasa del dia esta alta y aun asi hay parciales?', answer: 'Tasa = monto cobrado / monto esperado. Puedes tener monto alto cobrado (catch-up de dias previos) pero algunos cupones de hoy con cobro parcial. Por eso siempre revisar ambos numeros: tasa y conteo de parciales.' },
    { question: '¿Cuando se cierra "Hoy"?', answer: 'A medianoche en hora local. Antes de medianoche el dia sigue acumulando — la tasa puede subir en las ultimas horas si llegan pagos.' },
    { question: '¿Donde veo el detalle de cada cupon parcial?', answer: 'Click en la fila del dia desde la tabla "Cobranza Reciente" (proximamente). Por ahora, ir al credito desde Cartera y revisar el historial.' },
    { question: '¿Por que "Esperado Hoy" no incluye los morosos?', answer: 'A proposito. "Esperado" es solo lo que vence hoy. Lo moroso (vencido antes) tiene su propio espacio en Cartera y Latente para no mezclarlos en el reporte diario.' },
  ],
};

export const COLLECTION_DAY_HELP: PageHelpContent = {
  pageKey: 'collection-day',
  title: 'Dia de Cobro',
  description: 'Detalle completo de la cobranza de un dia especifico con todos los registros individuales.',
  capabilities: [
    'Seleccionar una fecha para ver su cobranza',
    'Ver el resumen de cobros completos, parciales y sin cobro',
    'Filtrar registros por estado de cobro',
    'Ver el detalle de cada cupon cobrado',
  ],
};

export const DELINQUENCY_HELP: PageHelpContent = {
  pageKey: 'delinquency',
  title: 'Morosidad',
  description: 'Analisis de morosidad por antiguedad. Muestra distribucion de creditos morosos por semanas de atraso.',
  capabilities: [
    'Ver total de creditos morosos y monto en mora',
    'Analizar la distribucion por semanas de atraso',
    'Identificar la concentracion de mora por rango',
    'Monitorear la tasa de morosidad general',
  ],
  terms: [
    { term: '1-2 semanas', definition: 'Mora temprana. Alta probabilidad de recuperacion.' },
    { term: '3-4 semanas', definition: 'Mora intermedia. Requiere seguimiento activo.' },
    { term: '5-6 semanas', definition: 'Mora avanzada. Riesgo elevado de default.' },
    { term: '7+ semanas', definition: 'Mora critica. Posible candidato a devolucion.' },
  ],
};

export const CARTERA_HELP: PageHelpContent = {
  pageKey: 'cartera',
  title: 'Cartera de Cobranza',
  description:
    'Lista de creditos con saldo pendiente — los que tienes que cobrar. Combina activos (al corriente), morosos y defaults en una sola vista. Excluye liquidados y cancelados.',
  capabilities: [
    'Ver de un vistazo cuantos creditos tienes vivos y cuantos estan en mora',
    'Filtrar por estado: Al corriente / En mora / Default',
    'Buscar por telefono del cliente o ID del credito',
    'Click en cualquier fila para abrir el detalle (amortizacion + historial de pagos)',
  ],
  sections: [
    {
      badge: 'Que incluye',
      badgeColor: 'blue',
      title: 'Creditos con saldo pendiente',
      description: 'A diferencia de "Creditos del Portafolio" que muestra TODOS los estados, Cartera filtra solo los que tienen algo que cobrar.',
      items: [
        {
          label: 'Al corriente (ACTIVE)', icon: 'check',
          description: 'Vigentes y sin cupones vencidos. La mayoria del portafolio deberia estar aqui.',
        },
        {
          label: 'En mora (DELINQUENT)', icon: 'warning',
          description: 'Cupones vencidos sin pagar. Entre 1 y 7 semanas de atraso. Accion: gestion de cobranza activa.',
        },
        {
          label: 'Default (DEFAULT)', icon: 'error',
          description: '8 semanas o mas de mora. Baja contable. Cobranza pasa a modo latente — gestion automatica sobre depositos.',
        },
      ],
    },
    {
      badge: 'Subtitulo dinamico',
      badgeColor: 'cyan',
      title: 'Lectura rapida del header',
      description: 'El subtitulo del header muestra "X creditos — Y al corriente, Z en mora". Si Z crece relativo a Y, hay deterioro de cartera.',
      items: [],
    },
    {
      badge: 'Flujo operativo',
      badgeColor: 'purple',
      title: 'Como usar esta pantalla en el dia a dia',
      description: 'Tipica rutina del equipo de cobranza:',
      items: [
        {
          label: '1. Filtrar por "En mora"', icon: 'check',
          description: 'Para ver el universo de gestion del dia.',
        },
        {
          label: '2. Click en credito a gestionar', icon: 'check',
          description: 'Abre detalle con tabla de amortizacion + historial de pagos. Identificas que cupon vencio y cuanto debe.',
        },
        {
          label: '3. Aplicar accion', icon: 'check',
          description: 'Llamada / SMS / push. Si el cliente paga ese dia, el credito vuelve a "Al corriente" automaticamente.',
        },
        {
          label: '4. Si lleva 8+ semanas sin pago', icon: 'warning',
          description: 'Pasa a Default → Cobranza Latente. Ya no se gestiona aqui sino en /collection/latent.',
        },
      ],
    },
  ],
  statuses: [
    { value: 'ACTIVE', label: 'Al corriente', description: 'Vigente y sin cupones vencidos.', variant: 'success' },
    { value: 'DELINQUENT', label: 'En mora', description: 'Tiene cupones vencidos. Gestion activa de cobranza.', variant: 'warning' },
    { value: 'DEFAULT', label: 'Default', description: '8+ semanas de mora consecutiva. Baja contable.', variant: 'danger' },
  ],
  terms: [
    { term: 'Saldo Pendiente', definition: 'Capital + interes + IVA aun no cobrados de los cupones futuros y vencidos.' },
    { term: 'Cupon', definition: 'Pago semanal individual del calendario de amortizacion. Vencido = paso su fecha sin pago completo.' },
    { term: 'Gestion vs Latente', definition: 'Cartera = gestion activa (llamadas, SMS). Latente = cobro automatico sobre depositos detectados (sin gestion humana).' },
  ],
  faq: [
    { question: '¿Por que no veo creditos liquidados aqui?', answer: 'Cartera = saldo pendiente. PAID_OFF y CANCELLED no tienen saldo, asi que viven en "Creditos del Portafolio" donde se ven TODOS los estados.' },
    { question: '¿Que diferencia hay con "Creditos del Portafolio"?', answer: 'Cartera filtra solo Active+Mora+Default (saldo pendiente). Creditos del Portafolio muestra todos incluyendo liquidados y cancelados — para vista historica completa.' },
    { question: '¿Por que un credito ACTIVE aparece en Cartera?', answer: 'Aunque este al corriente, sigue teniendo saldo pendiente (cupones futuros). La cartera no es solo morosos — es todo lo que falta cobrar.' },
    { question: '¿Como exporto la lista de morosos a llamar?', answer: 'Filtra por "En mora", luego copia los datos visibles. Si necesitas export formal, pide al equipo agregar el boton CSV.' },
  ],
};

export const LATENT_COLLECTION_HELP: PageHelpContent = {
  pageKey: 'latent-collection',
  title: 'Cobranza Latente',
  description:
    'Cobro AUTOMATICO sobre depositos detectados de creditos en default. Sin gestion humana. Cuando el cliente recibe un deposito a su wallet, el sistema retiene una porcion para abonar a la deuda original. Vista del desempeno de recuperacion + analisis por estado y antiguedad.',
  capabilities: [
    'Ver KPIs: total de creditos en default, deuda original, cobrado, pendiente y tasa de recuperacion',
    'Analizar la distribucion por estado dentro de los defaults',
    'Analizar la distribucion por antiguedad (cuando entraron a default)',
    'Ver el detalle de cada credito latente con su % de recuperacion individual',
    'Saltar al resumen financiero de la cartera defaulteada (link arriba)',
  ],
  sections: [
    {
      badge: 'Concepto',
      badgeColor: 'orange',
      title: '¿Que es cobranza latente?',
      description: 'Un credito que paso a DEFAULT (8+ semanas de mora) ya no se gestiona activamente — sale de Cartera. Pero la deuda no se condona: queda registrada como "lien" sobre la wallet del cliente.',
      items: [
        {
          label: 'Disparador', icon: 'check',
          description: 'Cuando llega un deposito a la wallet del cliente con lien activo, el sistema retiene un % automaticamente para abonar al credito en default.',
        },
        {
          label: 'Sin gestion humana', icon: 'check',
          description: 'No hay cobranza telefonica ni SMS. El cobro pasa solo si y cuando hay flujo en la wallet.',
        },
        {
          label: 'Recuperacion lenta', icon: 'warning',
          description: 'Por su naturaleza, un credito latente puede tardar meses en recuperarse parcialmente. Una tasa >50% se considera buena en este contexto.',
        },
      ],
    },
    {
      badge: 'KPIs',
      badgeColor: 'blue',
      title: 'Las 5 tarjetas principales',
      description: 'Resumen agregado del portafolio latente.',
      items: [
        { label: 'Creditos', description: 'Cantidad de creditos en estado DEFAULT (latente).' },
        { label: 'Deuda Original', description: 'Suma de los montos originales (capital + intereses calculados al desembolso).' },
        { label: 'Cobrado', description: 'Lo que ya se ha recuperado via cobranza latente desde que entraron a default.' },
        { label: 'Pendiente', description: 'Deuda Original − Cobrado. Es lo que aun esta por recuperar.' },
        { label: 'Tasa Recuperacion', description: 'Cobrado / Deuda Original × 100. KPI principal de calidad de la operacion latente.' },
      ],
    },
    {
      badge: 'Analisis',
      badgeColor: 'purple',
      title: 'Por Estado y por Antiguedad',
      description: 'Dos cortes complementarios para entender la composicion del portafolio latente:',
      items: [
        {
          label: 'Por Estado', icon: 'check',
          description: 'Dentro de los defaulteados, hay sub-estados. Ej. algunos siguen activos en latente, otros ya entraron a baja contable definitiva.',
        },
        {
          label: 'Por Antiguedad', icon: 'check',
          description: 'Buckets de semanas sin pago: 8-12, 13-20, 21-26, 26+. Cuanto mas viejo, menor probabilidad de recuperar — la concentracion en buckets viejos es senal de alerta.',
        },
      ],
    },
    {
      badge: 'Tabla detalle',
      badgeColor: 'cyan',
      title: 'Lectura de la tabla',
      description: 'Cada fila es un credito latente. Las columnas mas relevantes:',
      items: [
        {
          label: 'Recuperacion %', icon: 'check',
          description: 'Color verde si >=50%, ambar 25-49%, rojo <25%. Permite identificar rapido los casos perdidos vs los con avance.',
        },
        {
          label: 'Semanas Mora', icon: 'warning',
          description: 'Cuantas semanas lleva sin pagar (desde delinquency_start_date). Numero en rojo permanente — son ya defaults.',
        },
        {
          label: 'Ultima Actividad', icon: 'check',
          description: 'Fecha del ultimo evento sobre el credito (deposito, ajuste, etc.). Si es muy antigua, el cliente puede estar inactivo.',
        },
      ],
    },
  ],
  statuses: [
    { value: 'DEFAULTED', label: 'Default', description: 'Credito dado de baja contablemente. Cobranza solo via lien automatico.', variant: 'danger' },
    { value: 'WRITTEN_OFF', label: 'Castigado', description: 'Credito que paso al castigo definitivo. Recuperacion practicamente improbable.', variant: 'default' },
  ],
  terms: [
    { term: 'Lien', definition: 'Gravamen sobre la wallet del cliente. Permite retener automaticamente un % de cualquier deposito futuro hasta saldar la deuda.' },
    { term: 'Default', definition: 'Credito con 8+ semanas de mora consecutiva. Se da de baja contable y pasa a cobranza latente.' },
    { term: 'Tasa de Recuperacion', definition: 'Cobrado / Deuda original × 100. Mide cuanto se rescata de los creditos "perdidos".' },
    { term: 'Baja Contable', definition: 'Reconocimiento contable de que la deuda probablemente no se cobrara. No significa condonacion — el lien sigue activo.' },
    { term: 'Castigo', definition: 'Estado posterior a Default cuando se decide cerrar el caso. La recuperacion deja de gestionarse activamente.' },
  ],
  faq: [
    { question: '¿Por que aparece "Cobrado" si ya estan en default?', answer: 'Porque la cobranza latente sigue funcionando. Cada vez que el cliente recibe un deposito a wallet, se retiene una porcion automaticamente. Eso suma a "Cobrado" aunque no haya gestion activa.' },
    { question: '¿Que tasa de recuperacion es buena para latente?', answer: 'En cobranza latente, >50% se considera muy bueno. 25-50% es normal. <25% indica que la mayoria del portafolio defaulteado es practicamente irrecuperable.' },
    { question: '¿Cuando se "cierra" un credito latente?', answer: 'Cuando se recupera al 100% (raro) o cuando pasa a Castigo (decision contable). Mientras el lien siga activo, el credito sigue apareciendo aqui.' },
    { question: '¿Donde veo el resumen financiero de toda la cartera defaulteada?', answer: 'Click en el link "Ver resumen financiero de cartera en default" arriba del titulo. Lleva a la vista de Portafolio en Default.' },
  ],
};

// ─── Transfers ───────────────────────────────────────────────────────────────

export const COLLECTION_TRANSFERS_HELP: PageHelpContent = {
  pageKey: 'collection-transfers',
  title: 'Transferencias',
  description: 'Gestion de transferencias pendientes hacia UNE-Credito. Permite aprobar transferencias con referencia bancaria.',
  capabilities: [
    'Ver transferencias pendientes de aprobacion',
    'Aprobar transferencias con referencia bancaria',
    'Ver el desglose de principal e intereses',
    'Consultar el periodo cubierto por cada transferencia',
  ],
};

export const TRANSFER_HISTORY_HELP: PageHelpContent = {
  pageKey: 'transfer-history',
  title: 'Historial de Transferencias',
  description: 'Registro historico de todas las transferencias realizadas a UNE-Credito.',
  capabilities: [
    'Ver transferencias aprobadas y pendientes',
    'Consultar montos y periodos de cada transferencia',
    'Filtrar por estado de transferencia',
  ],
};

// ─── Funding ─────────────────────────────────────────────────────────────────

export const FUNDING_DESEMBOLSOS_HELP: PageHelpContent = {
  pageKey: 'funding',
  title: 'Fondeo / Desembolsos (UNE-Credito)',
  description:
    'Lado UNE-Credito del fondeo. FiadoSA ya desembolso los creditos al cliente con dinero propio — ahora UNE-Credito tiene que REPONER esos fondos a FiadoSA via SPEI. Aqui agrupas los pendientes y registras el envio del SPEI.',
  capabilities: [
    'Ver el monto total y cantidad de creditos pendientes de fondear (caja superior)',
    'Crear una instruccion de fondeo que agrupa todos los pendientes en un solo SPEI',
    'Registrar la clave de rastreo SPEI al confirmar el envio',
    'Consultar el historial de fondeos pasados con su estado actual',
    'Ver el detalle de creditos incluidos en cada fondeo (panel derecho)',
  ],
  statusFlow: {
    label: 'Ciclo de vida de una instruccion de fondeo',
    steps: [
      { value: 'PENDIENTE', color: 'amber', description: 'UNE-Credito creo la instruccion pero aun no ha mandado el SPEI.' },
      { value: 'ENVIADO', color: 'blue', description: 'UNE-Credito registro la clave de rastreo SPEI. Esperando confirmacion de FiadoSA.' },
      { value: 'CONFIRMADO', color: 'green', description: 'FiadoSA confirmo recepcion. Fondeo completo.' },
    ],
  },
  sections: [
    {
      badge: 'Tu rol',
      badgeColor: 'purple',
      title: 'Eres UNE-Credito',
      description: 'Esta pantalla es del lado UNE-Credito. La accion (mandar SPEI) la haces TU. Del otro lado, FiadoSA ve los SPEIs que les enviaste y los confirma desde "Confirmar Desembolsos".',
      items: [
        {
          label: '¿Por que UNE-Credito repone fondos a FiadoSA?', icon: 'check',
          description: 'FiadoSA pone el dinero al cliente al desembolsar (para no demorar la entrega). UNE-Credito, como fondeador, debe reembolsar esos desembolsos para que FiadoSA recupere el flujo.',
        },
      ],
    },
    {
      badge: 'Caja Pendiente',
      badgeColor: 'orange',
      title: 'Caja superior (la que tiene el boton Fondear)',
      description: 'Resume cuanto le debes a FiadoSA hoy. Naranja si hay pendientes, verde si esta en cero.',
      items: [
        {
          label: 'Monto total', icon: 'check',
          description: 'Suma de los desembolsos que aun no has fondeado. Click en el boton Fondear abre el modal para agrupar TODO en un solo SPEI.',
        },
        {
          label: 'Cantidad de creditos', icon: 'check',
          description: 'Cuantos creditos individuales se incluiran en la instruccion de fondeo.',
        },
      ],
    },
    {
      badge: 'Modal Fondear',
      badgeColor: 'blue',
      title: 'Que pides al confirmar el fondeo',
      description: 'Solo necesitas un dato: la clave de rastreo SPEI que generaste al hacer la transferencia bancaria.',
      items: [
        {
          label: 'Clave de Rastreo SPEI', icon: 'check',
          description: 'Identificador unico de hasta 30 caracteres asignado por tu banco al hacer el SPEI. Sirve para rastrear el pago en Banxico (CEP) y para que FiadoSA lo concilie.',
        },
        {
          label: 'Que pasa al confirmar', icon: 'check',
          description: 'Se crea una instruccion de fondeo en estado ENVIADO. Pasa al historial. FiadoSA la ve en su pantalla "Confirmar Desembolsos" y al confirmar, pasa a CONFIRMADO.',
        },
      ],
    },
    {
      badge: 'Historial',
      badgeColor: 'cyan',
      title: 'Lista inferior izquierda',
      description: 'Fondeos pasados ordenados por fecha descendente. Click en una fila para ver el detalle de creditos incluidos en el panel derecho.',
      items: [
        {
          label: 'Estado por fila',
          description: 'PENDIENTE = aun no has registrado SPEI. ENVIADO = SPEI registrado, esperando FiadoSA. CONFIRMADO = ciclo completo.',
        },
        {
          label: 'Referencia',
          description: 'Primeros 15 caracteres de la clave de rastreo. El resto se ve haciendo hover (title HTML).',
        },
      ],
    },
  ],
  statuses: [
    { value: 'PENDING', label: 'Pendiente', description: 'Instruccion creada pero sin SPEI registrado. Acción tuya: registrar la clave.', variant: 'warning' },
    { value: 'SENT', label: 'Enviado', description: 'SPEI registrado con su clave. Esperando confirmacion de FiadoSA.', variant: 'info' },
    { value: 'CONFIRMED', label: 'Confirmado', description: 'FiadoSA confirmo recepcion. Ciclo completo.', variant: 'success' },
  ],
  terms: [
    { term: 'Pendiente de Fondeo', definition: 'Credito ya desembolsado al cliente por FiadoSA pero UNE-Credito aun no repone los fondos.' },
    { term: 'Clave de Rastreo SPEI', definition: 'Identificador unico (hasta 30 chars) que tu banco asigna a la transferencia. Permite rastrear el pago en Banxico (banxico.org.mx/cep).' },
    { term: 'Instruccion de Fondeo', definition: 'Agrupacion de creditos en un solo SPEI. Una instruccion = un SPEI = una clave de rastreo.' },
    { term: 'CEP', definition: 'Constancia Electronica de Pago de Banxico. Comprueba que un SPEI se ejecuto. La conciliacion automatica futura usara el CEP.' },
  ],
  faq: [
    { question: '¿Que pasa si no hay creditos pendientes?', answer: 'La caja se pone verde con $0.00. No hay accion necesaria — vuelve cuando FiadoSA reporte nuevos desembolsos.' },
    { question: '¿Puedo fondear creditos individuales?', answer: 'No por ahora. El fondeo agrupa TODO lo pendiente en un solo SPEI. En el futuro se podra seleccionar.' },
    { question: '¿Que pasa si me equivoco con la clave de rastreo?', answer: 'Como FiadoSA aun no ha confirmado, queda como ENVIADO con clave incorrecta. Hay que pedir al equipo de FiadoSA que rechace para poder corregir. Verificar siempre antes de registrar.' },
    { question: '¿Cuando se considera completo el fondeo?', answer: 'Cuando FiadoSA confirma recepcion (estado CONFIRMADO). Mientras este en ENVIADO, el cierre depende de la confirmacion del otro lado.' },
    { question: '¿Como verifico que mi SPEI llego al banco de FiadoSA?', answer: 'Ingresa la clave de rastreo en banxico.org.mx/cep. La conciliacion automatica futura validara el CEP sin intervencion manual.' },
  ],
};

export const CONFIRMAR_DESEMBOLSOS_HELP: PageHelpContent = {
  pageKey: 'confirmar-desembolsos',
  title: 'Confirmar Desembolsos (FiadoSA)',
  description:
    'Lado FiadoSA del fondeo. Aqui revisas los SPEIs que UNE-Credito dice haber enviado y CONFIRMAS la recepcion en tu cuenta bancaria. Es el cierre del ciclo "UNE-Credito manda → FiadoSA recibe".',
  capabilities: [
    'Ver SPEIs registrados por UNE-Credito pendientes de tu confirmacion',
    'Verificar la clave de rastreo SPEI contra tu estado de cuenta bancario',
    'Confirmar recepcion con un click (icono check verde)',
    'Consultar historial de fondeos confirmados',
    'Ver detalle de creditos incluidos en cada fondeo (panel derecho)',
  ],
  statusFlow: {
    label: 'Tu rol en el ciclo',
    steps: [
      { value: 'ENVIADO', color: 'blue', description: 'UNE-Credito registro el SPEI. Te toca verificar y confirmar.' },
      { value: 'CONFIRMADO', color: 'green', description: 'Tu confirmaste recepcion. Ciclo completo.' },
    ],
  },
  sections: [
    {
      badge: 'Tu rol',
      badgeColor: 'purple',
      title: 'Eres FiadoSA',
      description: 'Esta pantalla es del lado FiadoSA. La accion (confirmar) la haces TU. UNE-Credito no puede cerrar el ciclo solo — necesita tu confirmacion para que el fondeo se considere completo.',
      items: [
        {
          label: '¿Por que tienes que confirmar?', icon: 'check',
          description: 'Para evitar discrepancias: aunque UNE-Credito registre que envio SPEI, el dinero pudo no llegar (rechazo bancario, error de CLABE, etc.). Tu confirmacion atestigua que el monto si entro a tu cuenta.',
        },
      ],
    },
    {
      badge: 'Antes de confirmar',
      badgeColor: 'amber',
      title: 'Verificacion previa',
      description: 'NUNCA confirmes a ciegas. Verifica primero contra tu estado de cuenta bancario:',
      items: [
        {
          label: '1. Cruzar clave de rastreo', icon: 'check',
          description: 'La columna "Referencia" muestra la clave que registro UNE-Credito. Buscala en tu estado de cuenta.',
        },
        {
          label: '2. Verificar monto', icon: 'check',
          description: 'El monto del SPEI debe coincidir EXACTO con el "Monto" de la fila. Diferencia de centavos = error a investigar.',
        },
        {
          label: '3. Verificar fecha', icon: 'check',
          description: 'La fecha del SPEI debe ser razonable vs cuando UNE-Credito lo registro. Si lleva varios dias sin aparecer en tu cuenta, hay un problema.',
        },
        {
          label: '4. Click en check verde', icon: 'check',
          description: 'Solo despues de validar 1-3, click en el icono. Cambia status a CONFIRMADO y mueve la fila al historial.',
        },
      ],
    },
    {
      badge: 'Vacio',
      badgeColor: 'green',
      title: '¿No hay nada pendiente?',
      description: 'Si la lista esta vacia, dice "Sin fondeos pendientes de confirmar — UNE-Credito aun no ha registrado envios". Es buena senal: significa que estas al dia o que UNE-Credito aun no manda lo siguiente.',
      items: [],
    },
  ],
  statuses: [
    { value: 'SENT', label: 'Enviado por UNE-Credito', description: 'Pendiente de tu confirmacion. Verifica en tu cuenta y confirma.', variant: 'info' },
    { value: 'CONFIRMED', label: 'Confirmado', description: 'Tu confirmaste recepcion. Ciclo cerrado.', variant: 'success' },
  ],
  terms: [
    { term: 'Confirmar Recepcion', definition: 'Atestiguar que el monto del SPEI llego efectivamente a tu cuenta bancaria de FiadoSA. No es lo mismo que "UNE-Credito dice que mando".' },
    { term: 'Estado de Cuenta', definition: 'Documento del banco de FiadoSA donde se ven los SPEIs entrantes con su clave de rastreo, monto y fecha. Es la fuente de verdad.' },
    { term: 'Clave de Rastreo', definition: 'Identificador SPEI generado por el banco de UNE-Credito. Sirve para cruzar contra tu estado de cuenta.' },
  ],
  faq: [
    { question: '¿Puedo confirmar sin verificar?', answer: 'Tecnicamente si, pero NO debes. La confirmacion es una declaracion contable. Si confirmas algo que no llego, queda un descuadre que aparecera en conciliacion. Confirma solo lo verificado.' },
    { question: '¿Que pasa si la clave no aparece en mi cuenta?', answer: 'No confirmes. Comunica a UNE-Credito para que verifique con su banco. Pudo haber un rechazo SPEI o un error en la clave registrada.' },
    { question: '¿Que pasa si el monto no coincide?', answer: 'No confirmes. Una diferencia significa que UNE-Credito registro mal el monto o que hubo un descuento bancario. Resolver primero antes de confirmar.' },
    { question: '¿Puedo des-confirmar?', answer: 'No desde el frontend. Si confirmaste por error, contacta al equipo de backend para revertir. Por eso verificar siempre.' },
  ],
};

export const COBROS_RECIBIDOS_HELP: PageHelpContent = {
  pageKey: 'cobros-recibidos',
  title: 'Cobros (FiadoSA)',
  description:
    'Lado FiadoSA del flujo de cobros. FiadoSA cobra al cliente (capital + interes total + IVA), retiene su comision (5/60 del interes) + IVA, y transfiere a UNE-Credito lo que le corresponde: principal + 55/60 del interes.',
  capabilities: [
    'Ver cobros acumulados pendientes de transferir a UNE-Credito',
    'Crear una transferencia agrupando los cobros de la semana',
    'Registrar la clave de rastreo SPEI al transferir',
    'Ver el desglose de cada credito (principal cobrado, interes, lo que se transfiere)',
    'Consultar historial de transferencias enviadas',
  ],
  statusFlow: {
    label: 'Ciclo de una transferencia FiadoSA → UNE-Credito',
    steps: [
      { value: 'PENDIENTE', color: 'amber', description: 'Cobros acumulados sin transferir aun.' },
      { value: 'ENVIADO', color: 'blue', description: 'FiadoSA registro SPEI a UNE-Credito.' },
      { value: 'CONFIRMADO', color: 'green', description: 'UNE-Credito confirmo recepcion.' },
    ],
  },
  sections: [
    {
      badge: 'Tu rol',
      badgeColor: 'purple',
      title: 'Eres FiadoSA',
      description: 'En este flujo, TU mandas el dinero a UNE-Credito. Es el espejo del fondeo: alli UNE-Credito te repone lo desembolsado; aqui tu le repones lo cobrado al cliente (menos tu comision).',
      items: [
        {
          label: '¿Que se transfiere?', icon: 'check',
          description: 'Principal cobrado + 55/60 del interes (parte de UNE-Credito). Tu retienes 5/60 del interes + 100% del IVA como comision por operar.',
        },
      ],
    },
    {
      badge: 'Frecuencia',
      badgeColor: 'cyan',
      title: 'Cuando ejecutar la transferencia',
      description: 'Tipicamente semanal (lunes). Se acumulan los cobros de la semana anterior.',
      items: [
        {
          label: 'Acumulacion', icon: 'check',
          description: 'Cada cobro recibido del cliente entre lunes y domingo se acumula. El siguiente lunes se transfiere el bloque completo.',
        },
        {
          label: 'No diaria', icon: 'warning',
          description: 'Hacer la transferencia diaria genera mas SPEIs y mas conciliaciones. La consolidacion semanal reduce friccion.',
        },
      ],
    },
    {
      badge: 'Desglose',
      badgeColor: 'blue',
      title: 'Que ve UNE-Credito cuando confirma',
      description: 'La transferencia trae el desglose por credito para que UNE-Credito pueda conciliar:',
      items: [
        {
          label: 'Principal', icon: 'check',
          description: 'Capital cobrado de cada cupon. Es el componente mas grande de la transferencia.',
        },
        {
          label: 'Intereses UNE-Credito (55/60)', icon: 'check',
          description: 'Parte de los intereses que corresponden a UNE-Credito segun el split definido en Operaciones / Definiciones.',
        },
        {
          label: 'Lo que NO va', icon: 'warning',
          description: 'Comision FiadoSA (5/60 del interes) + IVA total. Estos quedan en FiadoSA y NO viajan en el SPEI.',
        },
      ],
    },
    {
      badge: 'Vista detalle',
      badgeColor: 'green',
      title: 'Ver cobros (icono ojo)',
      description: 'En cada fila hay un icono de ojo. Click muestra el detalle de los cobros individuales que componen el monto. Util para conciliar cuando hay diferencias.',
      items: [],
    },
  ],
  statuses: [
    { value: 'PENDING', label: 'Pendiente', description: 'Cobros acumulados sin transferir aun. Tu accion: agruparlos y mandar SPEI.', variant: 'warning' },
    { value: 'SENT', label: 'Enviado', description: 'SPEI registrado a UNE-Credito. Esperando confirmacion del otro lado.', variant: 'info' },
    { value: 'CONFIRMED', label: 'Confirmado', description: 'UNE-Credito confirmo recepcion. Ciclo completo.', variant: 'success' },
  ],
  terms: [
    { term: 'Principal', definition: 'Capital cobrado de los creditos. Se transfiere 100% a UNE-Credito (es su dinero original).' },
    { term: 'Intereses UNE-Credito', definition: 'Parte del interes cobrado que corresponde a UNE-Credito segun el split (default: 55/60 = 91.67%).' },
    { term: 'Comision FiadoSA', definition: '5/60 del interes (~8.33%) + IVA. Es la remuneracion de FiadoSA por operar el credito. NO se transfiere.' },
    { term: 'Split', definition: 'Distribucion del interes entre Fiado (5 puntos base) y UNE-Credito (55 puntos base) sobre 60 totales. Configurable en Operaciones / Definiciones.' },
    { term: 'IVA', definition: '16% sobre la comision FiadoSA. Lo retiene FiadoSA y se entera al SAT.' },
  ],
  faq: [
    { question: '¿Cada cuanto se transfiere?', answer: 'Tipicamente semanal (lunes). Se acumulan los cobros de la semana anterior. Frecuencia exacta es decision operativa.' },
    { question: '¿Que pasa si me equivoco con la clave de rastreo?', answer: 'Igual que en Desembolsos: queda como ENVIADO con clave erronea. UNE-Credito no podra confirmar correctamente. Pedir al equipo de UNE-Credito que rechace para corregir.' },
    { question: '¿Por que retengo IVA si UNE-Credito es el dueno del interes?', answer: 'IVA grava la comision por servicio (operar el credito). Como tu eres quien presta el servicio, tu eres el responsable del IVA — no UNE-Credito. Por eso te quedas con el 100% del IVA y lo enteras al SAT.' },
    { question: '¿Donde veo el split actual?', answer: 'En Operaciones / Definiciones. El split es global por partner — no se puede cambiar credito por credito.' },
  ],
};

export const CONFIRMAR_COBROS_HELP: PageHelpContent = {
  pageKey: 'confirmar-cobros',
  title: 'Confirmar Cobros (UNE-Credito)',
  description:
    'Lado UNE-Credito del flujo de cobros. Aqui ves los SPEIs que FiadoSA dice haberte enviado (con los cobros del cliente) y los CONFIRMAS al verificarlos en tu cuenta bancaria.',
  capabilities: [
    'Ver SPEIs enviados por FiadoSA pendientes de tu confirmacion',
    'Verificar la clave de rastreo SPEI contra tu estado de cuenta',
    'Ver el desglose de cobros incluidos (icono ojo)',
    'Confirmar recepcion con un click',
    'Consultar historial de transferencias confirmadas',
  ],
  statusFlow: {
    label: 'Tu rol en el ciclo',
    steps: [
      { value: 'ENVIADO', color: 'blue', description: 'FiadoSA registro SPEI. Te toca verificar y confirmar.' },
      { value: 'CONFIRMADO', color: 'green', description: 'Tu confirmaste recepcion. Ciclo completo.' },
    ],
  },
  sections: [
    {
      badge: 'Tu rol',
      badgeColor: 'purple',
      title: 'Eres UNE-Credito',
      description: 'Esta pantalla es del lado UNE-Credito. La accion (confirmar) la haces TU. Es el espejo de "Confirmar Desembolsos" de FiadoSA — pero ahora el dinero te llega a TI.',
      items: [
        {
          label: '¿Que estas confirmando?', icon: 'check',
          description: 'Que recibiste el principal cobrado al cliente + 55/60 del interes. FiadoSA retiene 5/60 + IVA como comision (no viene en el SPEI).',
        },
      ],
    },
    {
      badge: 'Antes de confirmar',
      badgeColor: 'amber',
      title: 'Verificacion previa',
      description: 'Mismo principio que en cualquier confirmacion: NUNCA a ciegas.',
      items: [
        {
          label: '1. Cruzar clave de rastreo', icon: 'check',
          description: 'Buscar la clave que registro FiadoSA en tu estado de cuenta bancario.',
        },
        {
          label: '2. Verificar monto', icon: 'check',
          description: 'El monto SPEI debe coincidir con la fila. Si quieres validar el calculo (principal + interes 55/60), abre el detalle con el icono ojo.',
        },
        {
          label: '3. Validar desglose (opcional)', icon: 'check',
          description: 'Click en ojo: muestra credito por credito cuanto principal y cuanto interes te corresponde. Util si la suma no cuadra a primera vista.',
        },
        {
          label: '4. Click en check verde', icon: 'check',
          description: 'Solo despues de validar. Cambia status a CONFIRMADO.',
        },
      ],
    },
    {
      badge: 'Conciliacion contable',
      badgeColor: 'cyan',
      title: 'Por que el monto recibido es menor al cobrado',
      description: 'Importante: si comparas el monto que cobro FiadoSA al cliente vs lo que te llego, sera menor. Es esperado:',
      items: [
        {
          label: 'Lo que cobra FiadoSA al cliente', icon: 'check',
          description: 'Capital + interes total + IVA. Eso paga el cliente.',
        },
        {
          label: 'Lo que te transfiere a ti', icon: 'check',
          description: 'Capital + 55/60 del interes. Falta 5/60 del interes (comision FiadoSA) + IVA.',
        },
        {
          label: 'Diferencia esperada', icon: 'warning',
          description: '~8.33% del interes total + 16% IVA del interes total. Si la diferencia es otra, hay error de calculo.',
        },
      ],
    },
  ],
  statuses: [
    { value: 'SENT', label: 'Enviado por FiadoSA', description: 'Pendiente de tu confirmacion. Verifica en tu cuenta y confirma.', variant: 'info' },
    { value: 'CONFIRMED', label: 'Confirmado', description: 'Tu confirmaste recepcion. Ciclo cerrado.', variant: 'success' },
  ],
  terms: [
    { term: 'Confirmar Recepcion', definition: 'Atestiguar que el monto SPEI llego a tu cuenta de UNE-Credito. Equivale a una declaracion contable.' },
    { term: 'Desglose por credito', definition: 'Vista detallada (icono ojo) que muestra cuanto principal e interes te corresponden por cada credito incluido en el SPEI.' },
    { term: 'Diferencia esperada', definition: 'Lo que NO te llega del cobro al cliente: 5/60 del interes (comision FiadoSA) + 16% IVA del interes total.' },
  ],
  faq: [
    { question: '¿Por que el SPEI es menor que el cobrado al cliente?', answer: 'FiadoSA retiene su comision (5/60 del interes) y el IVA del interes. Solo te transfiere principal + 55/60 del interes. Es por diseno.' },
    { question: '¿Que pasa si no aparece el SPEI en mi cuenta?', answer: 'No confirmes. Comunica a FiadoSA para que verifique con su banco. Pudo haber un rechazo o error en la clave de rastreo registrada.' },
    { question: '¿Como conozco el detalle de los cobros?', answer: 'Click en el icono ojo de cada fila. Muestra credito por credito: cuanto principal y cuanto interes recibiste. Indispensable para conciliar tu contabilidad.' },
    { question: '¿Que ocurre si confirmo algo que no llego?', answer: 'Genera un descuadre que aparecera en la conciliacion. Confirma solo lo verificado contra tu estado de cuenta bancario.' },
  ],
};

export const FUNDING_INSTRUCTIONS_HELP: PageHelpContent = {
  pageKey: 'funding-instructions',
  title: 'Instrucciones de Fondeo',
  description: 'Lista de instrucciones de fondeo pendientes de confirmacion para desembolsar creditos.',
  capabilities: [
    'Ver instrucciones de fondeo pendientes',
    'Confirmar fondeo con referencia bancaria',
    'Ver montos y fechas de cada instruccion',
  ],
};

export const FUNDING_HISTORY_HELP: PageHelpContent = {
  pageKey: 'funding-history',
  title: 'Historial de Fondeo',
  description: 'Registro historico de todas las instrucciones de fondeo procesadas.',
  capabilities: [
    'Buscar fondeos por fecha',
    'Ver el estado de cada instruccion',
    'Navegar al detalle de un fondeo especifico',
  ],
};

export const FUNDING_DETAIL_HELP: PageHelpContent = {
  pageKey: 'funding-detail',
  title: 'Detalle de Fondeo',
  description: 'Vista completa de una instruccion de fondeo con todos sus datos y estado.',
  capabilities: [
    'Ver datos completos de la instruccion',
    'Confirmar el fondeo si esta pendiente',
    'Ver referencia bancaria si ya fue confirmado',
  ],
};

export const CIERRE_MENSUAL_FSA_HELP: PageHelpContent = {
  pageKey: 'cierre-mensual-fsa',
  title: 'Cierre Mensual — Libro Fiado SA',
  description:
    'Reporte contable mensual del lado FSA. Agrega los cobros que FSA recibe del cliente y transfiere a UNE-Credito por SPEI, distinguiendo cuanto se transfiere y cuanto queda en FSA por la retencion de intereses e IVA. Base flujo: todo por fecha del SPEI enviado a UNE.',
  capabilities: [
    'Ver el resumen anual mes por mes',
    'Click en un mes para el drill-down de transferencias',
    'Exportar CSV del mes seleccionado con detalle por transferencia',
    'Revisar el asiento contable sugerido del mes',
  ],
  sections: [
    {
      badge: 'Contexto contractual',
      badgeColor: 'blue',
      title: 'Por que hay dos libros distintos',
      description:
        'El contrato de credito se firma cliente ↔ UNE-Credito (SOFOM ENR). Inmediatamente UNE cede a FSA (SA de CV) via cesion con novacion. Por eso cada uno lleva su libro contable distinto:',
      items: [
        {
          label: 'FSA es sujeto pasivo del IVA', icon: 'check',
          description: 'Al recibir la cesion, FSA se vuelve acreedor y sujeto pasivo de IVA sobre los intereses que le corresponden. UNE (SOFOM) esta exento por LIVA Art. 15-X.',
        },
        {
          label: 'FSA cobra al cliente y transfiere a UNE', icon: 'check',
          description: 'El dinero entra a FSA (cobrador), FSA retiene su parte (int FSA + IVA) y envia el resto (principal + int UNE) por SPEI a UNE.',
        },
      ],
    },
    {
      badge: 'Columnas',
      badgeColor: 'emerald',
      title: 'Que muestra cada columna',
      items: [
        { label: 'Cobrado cliente', icon: 'check', description: 'Total pagado por los clientes en el mes (agregado por transfer).' },
        { label: 'Transfer UNE', icon: 'check', description: 'Monto SPEI enviado a UNE (principal + intereses UNE).' },
        { label: 'Int FSA', icon: 'check', description: 'Intereses generados a favor de FSA por la cesion.' },
        { label: 'IVA', icon: 'check', description: 'IVA sobre los intereses de FSA. FSA lo entera al SAT.' },
        { label: 'Retencion FSA', icon: 'check', description: 'Int FSA + IVA. Es lo que queda en FSA (no se transfiere a UNE).' },
        { label: 'Transfers', icon: 'check', description: 'Numero de SPEIs FSA→UNE del mes.' },
      ],
    },
    {
      badge: 'Asiento sugerido',
      badgeColor: 'amber',
      title: 'Estructura del asiento del mes',
      description: 'El detalle del mes muestra al fondo una sugerencia de asiento contable. Referencia — no autoritativo, siempre validar con contabilidad:',
      items: [
        { label: 'Debito Bancos', icon: 'check', description: '= total cobrado cliente (entrada real de efectivo).' },
        { label: 'Credito Ctas x pagar UNE', icon: 'check', description: '= transfer UNE (principal + intereses UNE).' },
        { label: 'Credito Ingreso por intereses', icon: 'check', description: '= intereses FSA (ingreso propio).' },
        { label: 'Credito IVA por pagar', icon: 'check', description: '= IVA sobre intereses FSA.' },
      ],
    },
  ],
  terms: [
    { term: 'Base flujo', definition: 'Reconocimiento contable por fecha real del cash (SPEI enviado), no por devengo. Adecuado para libros que deben cuadrar contra el estado de cuenta bancario.' },
    { term: 'Cesion con novacion', definition: 'Mecanismo por el cual el credito pasa de UNE a FSA cambiando la persona del acreedor. Es lo que hace a FSA sujeto pasivo del IVA sobre sus intereses.' },
    { term: 'Retencion FSA', definition: 'Parte del cobro que NO se transfiere a UNE porque corresponde a FSA (sus intereses + IVA).' },
  ],
  faq: [
    { question: '¿Por que este libro esta separado del de UNE?', answer: 'Contrato: cliente firma con UNE, UNE cede a FSA. Cada uno lleva libro por su parte de la operacion. FSA es sujeto pasivo IVA, UNE no.' },
    { question: '¿Que pasa si un transfer del mes esta en PENDING?', answer: 'Se incluye igual — el reporte agrupa PENDING/SENT/CONFIRMED. Base flujo por fecha del SPEI.' },
    { question: '¿Como armo el asiento del mes?', answer: 'Copia los totales del footer del detalle o usa el CSV. La sugerencia contable al fondo es referencia — validar con tu contador.' },
  ],
};

export const CIERRE_MENSUAL_UNE_HELP: PageHelpContent = {
  pageKey: 'cierre-mensual-une',
  title: 'Cierre Mensual — Libro UNE-Credito',
  description:
    'Reporte contable mensual del lado UNE (SOFOM). Muestra las SALIDAS cuando UNE fondea creditos nuevos y las ENTRADAS cuando FSA le transfiere cobros por cesion. Base flujo: todo por fecha del SPEI.',
  capabilities: [
    'Ver el resumen anual mes por mes con salidas, entradas y neto',
    'Click en un mes para el drill-down (fondeos + cobros)',
    'Exportar CSV del mes seleccionado',
    'Revisar los asientos contables sugeridos del mes',
  ],
  sections: [
    {
      badge: 'Contexto contractual',
      badgeColor: 'blue',
      title: 'El rol de UNE en el ciclo',
      description:
        'Cliente firma con UNE (SOFOM ENR). UNE cede a FSA via cesion con novacion — deja de ser acreedor operativo pero mantiene la propiedad economica de principal + intereses UNE:',
      items: [
        {
          label: 'Salidas: fondeo de creditos nuevos', icon: 'check',
          description: 'UNE envia SPEI a FSA para financiar los desembolsos del dia/semana. FundingInstruction (credit-payment-business).',
        },
        {
          label: 'Entradas: cobros recibidos por cesion', icon: 'check',
          description: 'FSA recibe los pagos del cliente y transfiere a UNE el principal + intereses UNE. TransfersToLoanCo.',
        },
        {
          label: 'Intereses UNE exentos de IVA', icon: 'check',
          description: 'Por LIVA Art. 15-X, los intereses de creditos otorgados por SOFOMs estan exentos. UNE cobra intereses libres de IVA.',
        },
      ],
    },
    {
      badge: 'Columnas',
      badgeColor: 'emerald',
      title: 'Que muestra cada columna',
      items: [
        { label: 'Fondeado (salidas)', icon: 'check', description: 'SPEI UNE→FSA del mes. Financian creditos nuevos.' },
        { label: 'Recibido (entradas)', icon: 'check', description: 'SPEI FSA→UNE del mes por cesion de cobros (principal + int UNE).' },
        { label: 'Neto', icon: 'check', description: 'Entradas - Salidas. Positivo = repago neto (cobros > fondeos). Negativo = crecimiento neto (fondeando mas de lo que cobra).' },
        { label: 'Principal', icon: 'check', description: 'Principal amortizado en las entradas del mes.' },
        { label: 'Int UNE', icon: 'check', description: 'Intereses UNE cobrados (exentos LIVA Art. 15-X).' },
      ],
    },
    {
      badge: 'Asiento sugerido',
      badgeColor: 'amber',
      title: 'Estructura del asiento del mes',
      description: 'El detalle del mes muestra al fondo la sugerencia contable — un bloque por fondeos y otro por cobros. Referencia — siempre validar con contabilidad:',
      items: [
        { label: 'Fondeos (salidas)', icon: 'check', description: 'Debito Cta x cobrar FSA / Credito Bancos.' },
        { label: 'Cobros (entradas)', icon: 'check', description: 'Debito Bancos / Credito Cta x cobrar FSA (principal) + Credito Ingreso por intereses (int UNE, exento LIVA).' },
      ],
    },
  ],
  terms: [
    { term: 'Base flujo', definition: 'Reconocimiento contable por fecha real del cash (SPEI enviado/recibido), no por devengo. Cuadra contra el estado de cuenta bancario.' },
    { term: 'LIVA Art. 15-X', definition: 'Los intereses de creditos otorgados por SOFOMs a personas fisicas para actividades empresariales estan exentos de IVA. Por eso UNE no cobra IVA sobre sus intereses.' },
    { term: 'Cesion con novacion', definition: 'Mecanismo por el cual el credito pasa de UNE a FSA cambiando la persona del acreedor. Es lo que separa el libro UNE del libro FSA.' },
    { term: 'Neto', definition: 'Entradas - Salidas del mes. Indica direccion del flujo: positivo = repago, negativo = crecimiento.' },
  ],
  faq: [
    { question: '¿Por que las entradas no incluyen IVA?', answer: 'UNE es SOFOM — sus intereses estan exentos por LIVA Art. 15-X. El IVA lo cobra y entera FSA (sujeto pasivo por la cesion).' },
    { question: '¿Por que los fondeos vienen de otra tabla?', answer: 'Los fondeos son FundingInstruction (credit-payment-business). El reporte hace cross-lambda para agregarlos junto con los cobros. Si la lambda no responde, silent-degrade a 0 en salidas.' },
    { question: '¿Que hacer si el neto es muy negativo?', answer: 'Es normal en fases de crecimiento (mucho fondeo nuevo). Verificar contra el presupuesto UNE y la politica de fondeo del mes.' },
  ],
};

// ─── Operations ──────────────────────────────────────────────────────────────

export const ORIGINATION_HELP: PageHelpContent = {
  pageKey: 'origination',
  title: 'Originacion de Creditos',
  description: 'Creditos originados por semana. Muestra la tendencia de nuevos creditos desembolsados.',
  capabilities: [
    'Ver creditos originados por semana',
    'Analizar montos totales y promedios semanales',
    'Identificar tendencias de crecimiento',
  ],
};

export const NURTURING_HELP: PageHelpContent = {
  pageKey: 'nurturing',
  title: 'Embudo de Nurturing',
  description: 'Conversion de usuarios registrados a creditos a traves del embudo de nurturing.',
  capabilities: [
    'Ver cada etapa del embudo de conversion',
    'Analizar tasas de conversion entre etapas',
    'Identificar cuellos de botella en el proceso',
  ],
};

export const PIPELINE_MATRIX_HELP: PageHelpContent = {
  pageKey: 'pipeline-matrix',
  title: 'Pipeline de Originacion',
  description: 'Tabla pivote que muestra la conversion de ofertas a creditos por nivel crediticio (Platinum, Gold, Silver, Bronze) a lo largo del tiempo.',
  capabilities: [
    'Ver el pipeline completo por semana: ofertas generadas, solicitudes creadas, creditos originados',
    'Analizar conversion y morosidad por nivel crediticio',
    'Comparar tendencias semana a semana con indicadores (▲/▼)',
    'Cambiar intervalo temporal: Semanal, Mensual o Semestral',
    'Click en headers O/S/C para navegar a las listas filtradas por nivel',
    'Ver promedios por nivel: ticket, plazo y tasa en el header de cada bloque',
    'Renglon de totales al fondo con conversion y morosidad acumulada',
  ],
  terms: [
    { term: 'Driver: la Oferta', definition: 'Todo se cuenta en la semana de la oferta. Si una oferta de W10 se convierte en solicitud en W11 y credito en W12, todo cuenta en W10. Asi se mide la conversion real.' },
    { term: 'Conversion O→C', definition: 'Porcentaje de ofertas que se convirtieron en credito: (creditos / ofertas) × 100.' },
    { term: 'Barra de Status (ST)', definition: 'Verde = creditos activos (al corriente). Azul = pagados. Rojo = morosos.' },
    { term: 'M (Morosidad)', definition: 'Porcentaje de creditos originados en esa semana que actualmente estan en mora.' },
    { term: 'Selector de Intervalo', definition: 'Semanal muestra cada semana. Mensual y Semestral agrupan las semanas, sumando ofertas/solicitudes/creditos y promediando conversiones.' },
  ],
  faq: [
    { question: '¿Por que las semanas recientes tienen baja conversion?', answer: 'Porque las ofertas recientes aun no han tenido tiempo de convertirse en solicitudes y creditos. La conversion "madura" con el tiempo.' },
    { question: '¿Que significa si Bronze tiene alta morosidad?', answer: 'Indica que los creditos de nivel Bronze se pagan peor. Puede sugerir que el scoring necesita ajustarse o que los limites de Bronze son demasiado permisivos.' },
  ],
};

export const SCORING_HELP: PageHelpContent = {
  pageKey: 'scoring',
  title: 'Inteligencia de Perfiles Crediticios',
  description: 'Analisis completo de los perfiles financieros: quienes son elegibles, como se distribuyen por nivel, y cuales son sus fuentes de ingreso.',
  capabilities: [
    'Ver KPIs de elegibilidad: total de perfiles, elegibles vs no elegibles, score promedio, credito promedio',
    'Analizar la distribucion de perfiles por nivel crediticio (Platinum, Gold, Silver, Bronze)',
    'Ver el histograma de scores (0-100) para entender la calidad general del portafolio',
    'Consultar el pipeline de credito: potencial de originacion basado en perfiles elegibles',
    'Explorar perfiles individuales con detalle de fuentes de ingreso',
    'Filtrar por elegibilidad y nivel, ordenar por Score/Ingreso/Credito',
  ],
  terms: [
    { term: 'Score Crediticio', definition: 'Puntaje 0-100 basado en: Estabilidad de Ingresos (30%), Capacidad de Pago (25%), Transferencias a Mexico (20%), Antiguedad (15%), Nivel de Ingresos (10%).' },
    { term: 'Elegible', definition: 'Perfil que cumple todos los criterios minimos definidos por UNE-Credito en Politicas.' },
    { term: 'Niveles', definition: 'Bronze (21-60), Silver (61-80), Gold (81-100), Platinum (100). Determina monto maximo de credito.' },
    { term: 'Potencial', definition: 'Suma de creditos maximos de elegibles. Cuanto podriamos originar si todos tomaran credito.' },
  ],
  faq: [
    { question: '¿Por que un perfil no es elegible?', answer: 'No cumple criterios de Politicas: ingreso minimo, antiguedad, frecuencia de depositos, etc. El detalle expandido muestra la razon.' },
    { question: '¿Cada cuanto se recalcula?', answer: 'Semanalmente via job WeeklyClientProcessing.' },
  ],
};

export const JOBS_HELP: PageHelpContent = {
  pageKey: 'jobs',
  title: 'Monitor de Jobs',
  description: 'Estado y ejecucion manual de jobs del sistema (lambdas programadas).',
  capabilities: [
    'Ver el estado de cada job del sistema',
    'Ejecutar jobs manualmente cuando sea necesario',
    'Monitorear la ultima ejecucion de cada job',
  ],
};

export const ALERTS_HELP: PageHelpContent = {
  pageKey: 'alerts',
  title: 'Alertas',
  description: 'Alertas operativas del sistema que requieren atencion.',
  capabilities: [
    'Ver alertas activas del sistema',
    'Identificar problemas que requieren accion',
    'Monitorear el estado operativo general',
  ],
};

export const RECONCILIATION_HELP: PageHelpContent = {
  pageKey: 'reconciliation',
  title: 'Conciliacion',
  description:
    'Mapa visual de los 3 puntos de conciliacion del sistema. Cada bloque cruza dos fuentes de verdad para detectar discrepancias. Click en un bloque del diagrama lleva a su pantalla de detalle.',
  capabilities: [
    'Ver el estado actual de las 3 conciliaciones (Collector, FSA, PCF)',
    'Identificar visualmente cuantos OK, descuadres y huerfanos hay en cada cruce',
    'Saltar al detalle de cualquier conciliacion para investigar',
    'Refrescar todos los contadores con un solo click',
  ],
  sections: [
    {
      badge: 'Concepto',
      badgeColor: 'blue',
      title: '¿Por que conciliar?',
      description: 'El dinero del credito viaja por varios sistemas: cliente → wallet → cobrador → FSA → UNE-Credito. En cada salto pueden haber discrepancias (lag, errores, fallas). Conciliar = verificar que ambos lados del salto coincidan.',
      items: [
        {
          label: 'Sin conciliacion = riesgo invisible', icon: 'warning',
          description: 'Sin estos cruces, un cobro podria registrarse contablemente pero el dinero NO llegar — y nadie se daria cuenta hasta el cierre mensual o auditoria. Conciliar lo detecta el mismo dia.',
        },
      ],
    },
    {
      badge: 'Los 3 cruces',
      badgeColor: 'cyan',
      title: 'Cada bloque del diagrama',
      description: 'El diagrama muestra los 3 puntos de conciliacion. Cada uno tiene su propia pantalla de detalle:',
      items: [
        {
          label: 'Collector (CC ↔ CR)', icon: 'check',
          description: 'Verifica que cada CreditContract (registro contable del cobro) tenga su CollectionRecord (registro operativo del cupon). Es el cruce mas critico — abarca el 100% de los cobros.',
        },
        {
          label: 'Fiado SA (Transfer ↔ Banco)', icon: 'check',
          description: 'Verifica que cada transferencia que FSA dice haber hecho aparezca como cargo en su estado de cuenta bancario. Detecta SPEIs registrados pero no enviados (o viceversa).',
        },
        {
          label: 'Pago Confiado (UNE-Credito → FSA)', icon: 'check',
          description: 'Verifica que cada transferencia entrante que UNE-Credito declara aparezca como abono en el estado de cuenta de FSA. Detecta fondeos declarados pero no recibidos.',
        },
      ],
    },
    {
      badge: 'Lectura',
      badgeColor: 'green',
      title: 'Como leer los contadores',
      description: 'Cada bloque tiene 4 numeros — la lectura es:',
      items: [
        { label: 'OK (verde)', icon: 'check', description: 'Cuadran — ambos lados coinciden con tolerancia de ±$1.00 MXN. Es lo que esperamos ver en la mayoria.' },
        { label: 'Solo X (rojo)', icon: 'error', description: 'Existe en un lado pero NO en el otro. Casi siempre es error — investigar inmediatamente.' },
        { label: 'Descuadre (amarillo)', icon: 'warning', description: 'Existe en ambos lados pero los montos NO coinciden. Puede ser cobro en proceso o error real.' },
      ],
    },
  ],
  terms: [
    { term: 'Conciliacion', definition: 'Proceso de verificar que dos fuentes de verdad coincidan. En finanzas: comprobar que lo que dice un sistema coincide con lo que dice otro.' },
    { term: 'Tolerancia', definition: 'Diferencia maxima aceptable para considerar OK (default ±$1.00 MXN para cubrir redondeos USD↔MXN).' },
    { term: 'Huerfano', definition: 'Registro que existe en un lado del cruce pero no tiene contraparte. Categorizado como "Solo X" segun el lado donde aparece.' },
    { term: 'CEP', definition: 'Constancia Electronica de Pago de Banxico. Comprobante de un SPEI. La conciliacion FSA y PCF usan claves de rastreo SPEI.' },
  ],
  faq: [
    { question: '¿Cada cuanto se ejecuta la conciliacion?', answer: 'A demanda — click en "Refrescar" en cada pantalla de detalle. No hay cron automatico (todavia). Recomendado: revisar diariamente las 3.' },
    { question: '¿Que hago si veo un huerfano (Solo X)?', answer: 'Investigar inmediatamente. Click en el bloque para ir al detalle, identifica el registro especifico, y reporta al equipo de backend con el ID.' },
    { question: '¿Por que las pantallas FSA y PCF estan en mock data?', answer: 'Estan en placeholder mode hasta que Dashboard Fiado agregue la categoria `une-credito` a sus reglas — ver PENDIENTES.md item 012. Mientras, los contadores muestran ceros o data simulada.' },
    { question: '¿Que pasa si una conciliacion lleva dias con descuadre?', answer: 'Es bandera roja. Si despues de 24-48hrs un descuadre persiste, investigar como problema sistemico (no como un cobro tardio). Reportar.' },
  ],
};

export const RECONCILIATION_FSA_HELP: PageHelpContent = {
  pageKey: 'reconciliation-fsa',
  title: 'Conciliacion Fiado SA (Transfer ↔ Banco)',
  description:
    'Verifica que cada transferencia SPEI que FSA dice haber enviado aparezca como CARGO en su estado de cuenta bancario. Es la conciliacion del lado FSA → afuera (cobros que FSA manda a UNE-Credito).',
  capabilities: [
    'Ver el resumen anual mes por mes en una matriz',
    'Click en cualquier celda para ver el detalle de transferencias de ese mes',
    'Comparar lado a lado: lo que el sistema declaro vs lo que el banco registro',
    'Cambiar de ano via el selector',
    'Registrar la conciliacion como revisada (LIMPIO o CON_DISCREPANCIAS)',
  ],
  sections: [
    {
      badge: 'Que cruza',
      badgeColor: 'emerald',
      title: 'Transfer (sistema) vs Cargo (banco)',
      description: 'Dos fuentes de verdad para el mismo evento:',
      items: [
        {
          label: 'Lado izquierdo: Transfer', icon: 'check',
          description: 'Lo que FSA registro en sistema cuando hizo SPEI a UNE-Credito (clave de rastreo + monto + fecha). Vive en `Transfers_GT`.',
        },
        {
          label: 'Lado derecho: Cargo bancario', icon: 'check',
          description: 'Lo que aparece en el estado de cuenta de FSA como salida (cargo) con la misma clave de rastreo. Viene del cuadre BBVA en S3.',
        },
        {
          label: 'Match key', icon: 'check',
          description: '`bank_reference` del Transfer = `claveRastreo` del cargo bancario. Si coinciden y los montos cuadran (±$1.00) → OK.',
        },
      ],
    },
    {
      badge: 'Resultados',
      badgeColor: 'green',
      title: '4 categorias de resultado',
      items: [
        { label: 'OK (verde)', icon: 'check', description: 'Transfer + cargo coinciden. SPEI ejecutado correctamente.' },
        {
          label: 'Solo Transfer (rojo)', icon: 'error',
          description: 'FSA dice haber enviado pero NO aparece cargo en el banco. Posible: SPEI rechazado, registro sin envio real, error operativo.',
          conditions: [{ label: 'Investigar inmediato', items: ['Verificar status SPEI en banca electronica', 'Reintentar o cancelar el registro segun corresponda'], severity: 'error' }],
        },
        {
          label: 'Solo Banco (rojo)', icon: 'error',
          description: 'Hay un cargo bancario sin Transfer asociado. Posible: SPEI manual fuera del sistema, comision bancaria, transferencia a otro destino mal categorizada.',
          conditions: [{ label: 'Posibles causas', items: ['Transferencia hecha sin pasar por sistema', 'Comision/cargo automatico del banco', 'SPEI a destino distinto categorizado mal'], severity: 'error' }],
        },
        {
          label: 'Descuadre (amarillo)', icon: 'warning',
          description: 'Existe en ambos lados pero los MONTOS no coinciden. Diferencia >$1.00 MXN.',
        },
      ],
    },
    {
      badge: 'Estado actual',
      badgeColor: 'amber',
      title: 'Placeholder mode',
      description: 'Esta pantalla esta operando con MOCK DATA. La integracion real con el cuadre BBVA depende de que Dashboard Fiado agregue la categoria `une-credito` (ver PENDIENTES.md item 012). Mientras, los contadores muestran data simulada.',
      items: [],
    },
  ],
  terms: [
    { term: 'Transfer', definition: 'Registro en sistema de un SPEI hecho por FSA. Tiene clave de rastreo, monto, fecha y destino.' },
    { term: 'Cargo bancario', definition: 'Movimiento de salida en el estado de cuenta de FSA con la misma clave de rastreo del Transfer.' },
    { term: 'claveRastreo', definition: 'Identificador unico del SPEI asignado por el banco. Es la llave para matchear ambos lados.' },
    { term: 'Tolerancia', definition: '±$1.00 MXN. Cubre redondeos y conversiones. Diferencias mayores caen como Descuadre.' },
  ],
  faq: [
    { question: '¿Por que veo ceros en todos los contadores?', answer: 'Placeholder mode — la integracion con el cuadre BBVA esta pendiente del lado Dashboard Fiado. Ver PENDIENTES.md item 012.' },
    { question: '¿Cual es la diferencia con la pantalla de PCF?', answer: 'FSA = SALIDAS de FSA hacia UNE-Credito (cobros transferidos). PCF = ENTRADAS a FSA desde UNE-Credito (fondeos recibidos). Direccion opuesta del flujo.' },
    { question: '¿Cuando registro la conciliacion como revisada?', answer: 'Despues de revisar todas las celdas de un mes y resolver/reconocer las discrepancias. El registro deja huella auditable de que el periodo fue revisado.' },
  ],
};

export const RECONCILIATION_PCF_HELP: PageHelpContent = {
  pageKey: 'reconciliation-pcf',
  title: 'Conciliacion Pago Confiado (UNE-Credito → FSA)',
  description:
    'Verifica que cada transferencia entrante que UNE-Credito dice haber enviado aparezca como ABONO en el estado de cuenta de FSA. Es la conciliacion del lado afuera → FSA (fondeos que UNE-Credito manda a FSA).',
  capabilities: [
    'Ver el resumen anual mes por mes',
    'Click en celda para ver el detalle del mes',
    'Comparar transferencias declaradas por UNE-Credito vs abonos en cuenta de FSA',
    'Cambiar de ano via el selector',
    'Registrar la conciliacion como revisada',
  ],
  sections: [
    {
      badge: 'Que cruza',
      badgeColor: 'cyan',
      title: 'Transfer UNE-Credito (sistema) vs Abono (banco FSA)',
      description: 'El espejo de la conciliacion FSA, pero en direccion opuesta:',
      items: [
        {
          label: 'Lado izquierdo: Transfer UNE-Credito', icon: 'check',
          description: 'Lo que UNE-Credito registro en sistema cuando hizo SPEI a FSA (clave de rastreo + monto + fecha). Tipicamente fondeos para cubrir desembolsos.',
        },
        {
          label: 'Lado derecho: Abono bancario', icon: 'check',
          description: 'Lo que aparece en el estado de cuenta de FSA como ENTRADA (abono) con la misma clave de rastreo. Viene del cuadre BBVA en S3.',
        },
        {
          label: 'Match key', icon: 'check',
          description: '`bank_reference` del Transfer = `claveRastreo` del abono bancario. Si coinciden y montos cuadran (±$1.00) → OK.',
        },
      ],
    },
    {
      badge: 'Resultados',
      badgeColor: 'green',
      title: '4 categorias de resultado',
      items: [
        { label: 'OK (verde)', icon: 'check', description: 'Transfer + abono coinciden. Fondeo recibido correctamente.' },
        {
          label: 'Solo Transfer (rojo)', icon: 'error',
          description: 'UNE-Credito dice haber enviado pero NO aparece abono en cuenta de FSA. Posible: SPEI rechazado o no enviado realmente, clave de rastreo erronea.',
          conditions: [{ label: 'Comunicar a UNE-Credito', items: ['Verificar status SPEI en su banca', 'Confirmar destino correcto (CLABE de FSA)'], severity: 'error' }],
        },
        {
          label: 'Solo Banco (rojo)', icon: 'error',
          description: 'Hay un abono bancario sin Transfer asociado de UNE-Credito. Posible: transferencia de otro origen, deposito manual, devolucion bancaria.',
          conditions: [{ label: 'Posibles causas', items: ['Transferencia de otro partner/origen', 'Devolucion bancaria de SPEI rechazado', 'Deposito manual no registrado'], severity: 'error' }],
        },
        {
          label: 'Descuadre (amarillo)', icon: 'warning',
          description: 'Ambos lados existen pero los MONTOS no coinciden. Diferencia >$1.00 MXN.',
        },
      ],
    },
    {
      badge: 'Estado actual',
      badgeColor: 'amber',
      title: 'Placeholder mode',
      description: 'Igual que FSA: la pantalla opera con MOCK DATA hasta que Dashboard Fiado clasifique los SPEIs entrantes con categoria `une-credito` (ver PENDIENTES.md item 012). Tambien depende de que UNE-Credito tenga banco/CLABE asignados (item 003).',
      items: [],
    },
  ],
  terms: [
    { term: 'Transfer UNE-Credito', definition: 'Registro en sistema de un SPEI hecho POR UNE-Credito hacia FSA. Tipicamente fondeo de creditos.' },
    { term: 'Abono bancario', definition: 'Movimiento de entrada en el estado de cuenta de FSA con la misma clave de rastreo.' },
    { term: 'Pago Confiado', definition: 'Nombre comercial del flujo de fondeo entrante. Tecnicamente: SPEIs entrantes a la cuenta concentradora de FSA categorizados como `une-credito`.' },
    { term: 'CLABE de FSA', definition: 'Cuenta concentradora donde FSA recibe los fondeos. UNE-Credito debe usar esta CLABE como destino del SPEI.' },
  ],
  faq: [
    { question: '¿Cual es la diferencia con FSA?', answer: 'PCF = ENTRADAS a FSA (fondeos que recibe). FSA = SALIDAS de FSA (transferencias que envia). Mismo mecanismo, direccion opuesta del flujo.' },
    { question: '¿Por que la pantalla esta en mock data?', answer: 'Bloqueada por dos cosas: (1) Dashboard Fiado debe agregar regla `une-credito` (PENDIENTES item 012) y (2) UNE-Credito debe tener banco/CLABE asignados (PENDIENTES item 003).' },
    { question: '¿Que es "Pago Confiado"?', answer: 'Nombre comercial del flujo de fondeo. PCF como acronimo se usa internamente. Tecnicamente son SPEIs categorizados como `une-credito` en el cuadre BBVA.' },
  ],
};

export const RECONCILIATION_COLLECTOR_HELP: PageHelpContent = {
  pageKey: 'reconciliation-collector',
  title: 'Conciliacion Collector',
  description: 'Verifica que cada cobro tenga su registro contable (CreditContract) y su registro operativo (CollectionRecord), y que los montos cuadren entre ambos.',
  capabilities: [
    'Ver el resumen anual por mes: cuantos cobros cuadran y cuantos no',
    'Click en cualquier celda para ver el detalle de ese mes',
    'Comparar lado a lado el contrato vs los registros de cobro',
    'Exportar el detalle de cualquier mes a CSV',
    'Registrar la conciliacion como revisada (LIMPIO o CON_DISCREPANCIAS)',
  ],
  statusFlow: {
    label: 'Ciclo de vida del CreditContract',
    steps: [
      { value: 'ORIGINADO', color: 'amber', description: 'Credito creado, pendiente de venta al cobrador' },
      { value: 'COMPRADO', color: 'purple', description: 'Cobrador compro el credito' },
      { value: 'COBRADO', color: 'blue', description: 'Cobrador pago a Fiado' },
      { value: 'SALDADO', color: 'green', description: 'Proceso completado' },
    ],
  },
  sections: [
    {
      badge: 'Ejemplo',
      badgeColor: 'blue',
      title: 'Como funciona el cobro',
      description: 'Cuando un cliente paga un cupon, se crean dos registros en paralelo. Esta conciliacion verifica que ambos existan y cuadren.',
      items: [
        {
          label: 'Paso 1: El cliente paga', icon: 'check',
          description: 'El cliente paga su cupon semanal de $594.76 MXN via wallet USD. El cobrador (collector) procesa el pago.',
        },
        {
          label: 'Paso 2: Se crea el CreditContract (CC)', icon: 'check',
          description: 'El collector crea un registro contable: "cobré $594.76 del credito XYZ". Este es el lado del dinero.',
        },
        {
          label: 'Paso 3: Se crea el CollectionRecord (CR)', icon: 'check',
          description: 'El credit-worker crea un registro operativo: "cupon #3 del credito XYZ fue pagado, monto $594.76". Este es el lado operativo.',
        },
        {
          label: 'Paso 4: Nosotros verificamos', icon: 'check',
          description: 'Buscamos todos los CRs que apuntan a cada CC y comparamos montos. Si cuadran (tolerancia ±$1.00) → OK. Si no → hay un problema.',
        },
      ],
    },
    {
      badge: 'Resultados',
      badgeColor: 'green',
      title: '4 resultados posibles',
      description: 'Cada CreditContract cae en una de estas categorias:',
      items: [
        {
          label: 'OK', icon: 'check',
          description: 'El CC tiene sus CRs y los montos cuadran. Ej: CC dice $594.76, los CRs suman $594.76.',
        },
        {
          label: 'Descuadre', icon: 'warning',
          description: 'El CC tiene CRs pero los montos NO cuadran. Puede ser que los CRs sumen de menos (cobro incompleto) o de mas (cobro duplicado).',
          conditions: [
            { label: 'Posibles causas', items: ['Cobro en proceso que aun no se completa', 'Diferencia de redondeo USD→MXN fuera de tolerancia', 'Un CR duplicado o faltante'], severity: 'error' },
          ],
        },
        {
          label: 'Solo CC', icon: 'error',
          description: 'Existe el contrato contable pero ningun registro operativo lo respalda. Es como si hubiera un recibo de cobro sin que nadie haya cobrado.',
          conditions: [
            { label: 'SIEMPRE es error', items: ['El dinero se registro pero no hay evidencia operativa del cobro', 'Investigar de inmediato con el equipo de backend'], severity: 'error' },
          ],
        },
        {
          label: 'Solo CR', icon: 'error',
          description: 'Existe el registro operativo pero el contrato contable al que apunta no existe. Es como si se hubiera cobrado pero no se genero el recibo.',
          conditions: [
            { label: 'SIEMPRE es error', items: ['El cobro se hizo pero no hay registro contable', 'El CC pudo haberse eliminado o nunca se creo'], severity: 'error' },
          ],
        },
      ],
    },
    {
      badge: 'Tipos de pago',
      badgeColor: 'purple',
      title: 'Un CC puede tener 1 o varios CRs',
      description: 'Dependiendo de como paga el cliente, un solo CreditContract puede cubrir uno o varios cupones:',
      items: [
        { label: 'REGULAR', icon: 'check', description: 'Pago normal semanal. 1 CC cubre 1 cupon → 1 CR.' },
        { label: 'ADVANCED', icon: 'check', description: 'Pago adelantado. El cliente paga varios cupones de golpe. 1 CC → varios CRs (uno por cada cupon cubierto).' },
        { label: 'SETTLEMENT', icon: 'check', description: 'Liquidacion total. El cliente paga todo lo que debe. 1 CC → varios CRs (todos los cupones restantes).' },
        { label: 'ANTICIPATED', icon: 'check', description: 'Abono a capital. Reduce el saldo pendiente. 1 CC → 1 CR.' },
      ],
    },
    {
      badge: 'Otros cruces',
      badgeColor: 'slate',
      title: 'CC↔CV, CC↔Collector, Collector↔Banco',
      description: 'Los eslabones 2-4 de la cadena (contratos de cesion, transacciones del cobrador, y movimientos bancarios) se concilian en DB Conciliacion, que tiene acceso a todas las tablas y archivos bancarios.',
      items: [],
    },
  ],
  faq: [
    { question: '¿Que hago si veo un Solo CC o Solo CR?', answer: 'Es un error que requiere investigacion inmediata. Reportar al equipo de backend con el CC ID o CR ID para que verifiquen por que no se genero la contraparte.' },
    { question: '¿Que hago si veo un Descuadre?', answer: 'Verificar si es un cobro en proceso (el monto aun no se completa) o un problema real. Si la diferencia es mayor a $1.00 MXN, investigar. Puede ser un CR faltante o un cobro duplicado.' },
    { question: '¿Cada cuanto debo conciliar?', answer: 'Idealmente mensual. El boton "Registrar" guarda evidencia de que el periodo fue revisado, con status LIMPIO (sin errores) o CON_DISCREPANCIAS (con errores pendientes).' },
    { question: '¿Y los otros cruces (CV, Collector, Banco)?', answer: 'Esos los maneja DB Conciliacion. Esta vista solo verifica el primer eslabon: que todo cobro tenga tanto su registro contable como su registro operativo.' },
  ],
};

export const OPS_DEFINITIONS_HELP: PageHelpContent = {
  pageKey: 'ops-definitions',
  title: 'Definiciones Operativas',
  description:
    'Variables que gobiernan COMO opera el motor (no QUE prestar — eso son Politicas). Tres bloques: Distribucion Financiera (split de intereses + CLABEs), Cuentas Bancarias para conciliacion, y Parametros Operativos (umbrales de cobranza, deteccion de ingresos, clasificacion de frecuencia).',
  capabilities: [
    'Editar el split de intereses entre FiadoSA y UNE-Credito (puntos base + IVA)',
    'Registrar las CLABEs concentradoras de ambas partes para conciliacion bancaria',
    'Ajustar el umbral de mora para marcar default (baja contable)',
    'Configurar la ventana de analisis y deposito minimo para deteccion de ingresos',
    'Modificar rangos de clasificacion de frecuencia de depositos',
    'Cada cambio queda versionado — ver Historial',
  ],
  sections: [
    {
      badge: 'Politicas vs Definiciones',
      badgeColor: 'blue',
      title: 'Cual es la diferencia',
      description: 'Esta pantalla NO configura quien obtiene credito ni a que tasa. Eso vive en Configuracion / Politicas. Aqui se configura COMO opera el sistema una vez que ya hay creditos:',
      items: [
        {
          label: 'Politicas = QUE prestar', icon: 'check',
          description: 'Reglas crediticias: elegibilidad, scoring, niveles, tasas al cliente, limites de exposicion.',
        },
        {
          label: 'Definiciones = COMO operar', icon: 'check',
          description: 'Plomeria operativa: como se reparte el interes entre socios, en que cuenta cae el dinero, cuando se marca default, como se detectan ingresos del cliente.',
        },
      ],
    },
    {
      badge: 'Bloque 1',
      badgeColor: 'green',
      title: 'Distribucion Financiera (Split)',
      description: 'De la tasa total que paga el cliente, cuanto se queda FiadoSA y cuanto se va a UNE-Credito. Se mide en puntos base sobre el total.',
      items: [
        {
          label: 'Ejemplo tipico', icon: 'check',
          description: 'Fiado 5 puntos + UNE-Credito 55 puntos = 60 puntos totales. Cliente paga 60% anual nominal — Fiado se queda 8.33%, UNE-Credito 91.67%.',
        },
        {
          label: 'IVA', icon: 'check',
          description: '16% sobre el TOTAL del interes cobrado. Lo retiene FiadoSA y lo entera al SAT. NO se transfiere a UNE-Credito.',
        },
        {
          label: 'Sin retroactividad', icon: 'warning',
          description: 'Cambiar el split aqui SOLO afecta creditos originados despues del cambio. Los creditos vivos mantienen el split con el que se originaron.',
        },
      ],
    },
    {
      badge: 'Bloque 2',
      badgeColor: 'cyan',
      title: 'Cuentas Bancarias',
      description: 'CLABEs concentradoras para identificar movimientos en estados de cuenta y poder conciliar. Cada parte tiene su CLABE.',
      items: [
        {
          label: 'CLABE FiadoSA', icon: 'check',
          description: 'Cuenta principal de FSA donde caen los cobros del cliente y desde donde se transfiere a UNE-Credito.',
        },
        {
          label: 'CLABE UNE-Credito', icon: 'check',
          description: 'Cuenta de UNE-Credito a donde FSA transfiere los cobros (segun split). Tambien es el origen de los fondeos hacia FSA.',
        },
        {
          label: 'Para que sirven', icon: 'check',
          description: 'Cruzar movimientos SPEI con sus claves de rastreo en las conciliaciones FSA y PCF. Sin estas CLABEs registradas, las conciliaciones no funcionan.',
        },
      ],
    },
    {
      badge: 'Bloque 3',
      badgeColor: 'purple',
      title: 'Parametros Operativos',
      description: 'Umbrales que gobiernan procesos automaticos del backend. Cambiarlos modifica el comportamiento de cobranza, scoring, y default sin requerir codigo.',
      items: [
        {
          label: 'Semanas para default', icon: 'warning',
          description: 'Cuantas semanas de mora consecutiva antes de marcar baja contable. Default 8. Reducirlo = mas defaults rapido. Aumentarlo = mas tiempo de cobranza activa.',
        },
        {
          label: 'Ventana de ingresos (dias)', icon: 'check',
          description: 'Cuantos dias hacia atras analiza el sistema para detectar patron de depositos del cliente. Default 90. Mas largo = mas data pero mas lento responder a cambios.',
        },
        {
          label: 'Deposito minimo', icon: 'check',
          description: 'Monto minimo USD para considerar un deposito como "ingreso valido" en el analisis P1. Filtra micro-transacciones que no son sueldo.',
        },
        {
          label: 'Frecuencia (rangos)', icon: 'check',
          description: 'Como se clasifica el patron de depositos: Diaria / Semanal / Quincenal / Mensual. Configura los rangos de dias entre depositos para cada categoria.',
        },
        {
          label: 'Min depositos', icon: 'check',
          description: 'Cantidad minima de depositos en la ventana para considerar al cliente como "con patron de ingresos". Menos = no califica para scoring de estabilidad.',
        },
      ],
    },
  ],
  terms: [
    { term: 'Split', definition: 'Distribucion del interes entre FiadoSA y UNE-Credito. Se mide en puntos base sobre el total cobrado.' },
    { term: 'Puntos base', definition: 'Unidad de medida del split. 5 + 55 = 60 puntos totales. Es proporcion, no porcentaje absoluto.' },
    { term: 'CLABE', definition: 'Clave Bancaria Estandarizada de 18 digitos que identifica una cuenta bancaria en Mexico.' },
    { term: 'Default / Baja contable', definition: 'Credito con mora prolongada (default 8 semanas) que se da por perdido contablemente. Pasa a cobranza latente.' },
    { term: 'Ventana de ingresos', definition: 'Periodo hacia atras (en dias) que el sistema analiza para detectar patron de depositos del cliente.' },
    { term: 'Frecuencia', definition: 'Clasificacion del patron de depositos: Diaria, Semanal, Quincenal, Mensual. Se usa en el scoring de Estabilidad de Ingresos.' },
    { term: 'P1', definition: 'Proceso interno de deteccion de ingresos. "P" viene de la nomenclatura de procesos del modelo de credito.' },
    { term: 'Sin retroactividad', definition: 'Principio del motor: cambios aqui afectan SOLO procesos futuros. Lo ya ejecutado no se recalcula.' },
  ],
  faq: [
    { question: '¿Cual es la diferencia entre esta pantalla y Configuracion / Politicas?', answer: 'Politicas = reglas CREDITICIAS (a quien, cuanto, a que tasa). Definiciones = reglas OPERATIVAS (split entre socios, CLABEs, umbrales internos). No se mezclan.' },
    { question: '¿Que pasa si cambio el split?', answer: 'Aplica solo a creditos NUEVOS. Los creditos vivos mantienen el split con el que se originaron — esta congelado en el `Credit` al momento del desembolso.' },
    { question: '¿Que pasa si reduzco las semanas para default?', answer: 'Mas creditos pasaran a default mas rapido — saldo se considerara perdido, cobranza activa se detendra y pasara a latente. Cuidado: cambiar agresivamente puede generar mucho default de golpe.' },
    { question: '¿Para que sirven las CLABEs si la conciliacion ya funciona?', answer: 'La conciliacion necesita saber CONTRA QUE cuentas comparar los movimientos. Sin CLABEs registradas, no hay como filtrar las transacciones relevantes en el cuadre BBVA.' },
    { question: '¿Donde veo el historial de cambios?', answer: 'En Configuracion / Historial (mismo historial que politicas — esta filtrado por configType para mostrar solo definiciones operativas cuando llegas desde aqui).' },
    { question: '¿Por que la ventana de ingresos default es 90 dias?', answer: 'Balance: suficiente para detectar patron mensual con margen, no tan largo que perdamos sensibilidad a cambios recientes (ej. perdida de empleo). Calibrado empiricamente.' },
  ],
};

export const HELP_CONTENT: Record<string, PageHelpContent> = {
  // Credit
  'credit-dashboard': CREDIT_DASHBOARD_HELP,
  'credits-list': CREDITS_LIST_HELP,
  'credit-detail': CREDIT_DETAIL_HELP,
  'applications-list': APPLICATIONS_LIST_HELP,
  'application-detail': APPLICATION_DETAIL_HELP,
  'offers-list': OFFERS_LIST_HELP,
  'offer-detail': OFFER_DETAIL_HELP,
  'financial-profile': FINANCIAL_PROFILE_HELP,
  'policies': POLICIES_HELP,
  'policies-eligibility': POLICIES_ELIGIBILITY_HELP,
  'policies-scoring': POLICIES_SCORING_HELP,
  'policies-credit-levels': POLICIES_CREDIT_LEVELS_HELP,
  'policies-interest-rates': POLICIES_INTEREST_RATES_HELP,
  'policies-exposure-limits': POLICIES_EXPOSURE_LIMITS_HELP,
  'policies-whitelist': POLICIES_WHITELIST_HELP,
  'config-history': CONFIG_HISTORY_HELP,
  'simulator': SIMULATOR_HELP,
  // Portfolio
  'portfolio-dashboard': PORTFOLIO_DASHBOARD_HELP,
  'portfolio-historico': PORTFOLIO_HISTORICO_HELP,
  'portfolio-active': PORTFOLIO_ACTIVE_HELP,
  'portfolio-delinquent': PORTFOLIO_DELINQUENT_HELP,
  'portfolio-defaulted': PORTFOLIO_DEFAULTED_HELP,
  // Collection
  'collection-dashboard': COLLECTION_DASHBOARD_HELP,
  'cartera': CARTERA_HELP,
  'collection-day': COLLECTION_DAY_HELP,
  'delinquency': DELINQUENCY_HELP,
  'latent-collection': LATENT_COLLECTION_HELP,
  // Transfers
  'collection-transfers': COLLECTION_TRANSFERS_HELP,
  'transfer-history': TRANSFER_HISTORY_HELP,
  // Funding
  'funding': FUNDING_DESEMBOLSOS_HELP,
  'confirmar-desembolsos': CONFIRMAR_DESEMBOLSOS_HELP,
  'cobros-recibidos': COBROS_RECIBIDOS_HELP,
  'confirmar-cobros': CONFIRMAR_COBROS_HELP,
  'funding-instructions': FUNDING_INSTRUCTIONS_HELP,
  'funding-history': FUNDING_HISTORY_HELP,
  'funding-detail': FUNDING_DETAIL_HELP,
  'cierre-mensual-fsa': CIERRE_MENSUAL_FSA_HELP,
  'cierre-mensual-une': CIERRE_MENSUAL_UNE_HELP,
  // Operations
  'origination': ORIGINATION_HELP,
  'nurturing': NURTURING_HELP,
  'scoring': SCORING_HELP,
  'pipeline-matrix': PIPELINE_MATRIX_HELP,
  'jobs': JOBS_HELP,
  'alerts': ALERTS_HELP,
  'reconciliation': RECONCILIATION_HELP,
  'reconciliation-fsa': RECONCILIATION_FSA_HELP,
  'reconciliation-pcf': RECONCILIATION_PCF_HELP,
  'reconciliation-collector': RECONCILIATION_COLLECTOR_HELP,
  'ops-definitions': OPS_DEFINITIONS_HELP,
};
